import React, {useState, useEffect, useMemo, useRef} from 'react';
import {
  ScrollView,
  StyleSheet,
  Pressable,
  Platform,
  Linking,
  View,
  Alert,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCLoader from '@/Atoms/CCLoader';
import CCColors from '@/Utils/CCColors';
import {FontSizes, Spacing} from '@/Utils/Dimensions';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {useNavigation, useRoute} from '@react-navigation/native';
import {ScreenName} from '@/navigators/StackConstants';
import useGetRenewalForUser from '@/Hooks/RenewalApi/useGetRenewalForUser';
import useGetPricingForNewUser from '@/Hooks/RenewalApi/useGetPricingForNewUser';
import useGetUserLevels from '@/Hooks/RenewalApi/useGetUserLevels';

function getQueryParam(name: string): string | null {
  if (typeof window === 'undefined' || !window.location?.search) {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}
import useRequestCallback from '@/Hooks/RenewalApi/useRequestCallback';
import useValidateCoupon from '@/Hooks/RenewalApi/useValidateCoupon';
import DateTimeSlotModal, {SelectedSlot} from './components/DateTimeSlotModal';
import BatchSelectionModal, {
  SelectedBatch,
} from './components/BatchSelectionModal';
import RenewalHeader from './components/RenewalHeader';
import LevelTracker, {Level} from './components/LevelTracker';
import PlanSelector from './components/PlanSelector';
import DurationSelector from './components/DurationSelector';
import CoachSelector, {CoachType} from './components/CoachSelector';
import ViewBreakupModal from './components/ViewBreakupModal';
import OrderSummary from './components/OrderSummary';
import CouponModal from './components/CouponModal';
import PaymentConfirmationModal from './components/PaymentConfirmationModal';
import {SuccessPage} from './components/SuccessPage';
import AssignmentRequiredPage from './components/AssignmentRequiredPage';
import SupportRequiredModal from './components/SupportRequiredModal';
import WebinarDetailsModal from './components/WebinarDetailsModal';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import CCSelect from '@/Atoms/CCSelect';
import countryCodes from '@/Screens/Login/countryCodes.json';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
import {formatCurrency, setLocalStorage} from '@/Utils/CommonUtils';
import Spacer from '@/Atoms/Spacer';
import useAlertModalStore from '@/Store/AlertModalStore';
import {SUPPORT_REQUIRED_TOURNAMENT_IDS} from './constants/supportConstants';
import UserLevelRequiredPage from './components/UserLevelRequiredPage';

const billingCountrySelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: 48,
    borderWidth: 1.5,
    borderColor: state.isFocused
      ? CCColors.Primary.Default
      : CCColors.Grey.Divider200,
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(77, 63, 55, 0.08)',
    fontSize: FontSizes[15],
    '&:hover': {
      borderColor: CCColors.Primary.Default,
    },
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: '2px 14px',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: '#6B7280',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: CCColors.TextColor.Dark,
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 9999,
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 9999,
  }),
};

interface RenewalScreenProps {
  userData?: {
    user_id?: string;
  };
}

const RenewalScreen: React.FC<RenewalScreenProps> = ({userData}) => {
  const navigation = useNavigation();
  const route = useRoute();
  const params = (route.params as any) || {};
  const initialCouponCode = params.couponCode || getQueryParam('couponCode');
  const token = localStorage.getItem('oneClickToken');
  const [level, setLevel] = useState<string | null>(null);
  const [newUser, setNewUser] = useState<boolean>(true);
  const isNewPaymentPage = (route as any).name === 'CsocNewPayment';
  // Old renewal flow: when level is absent we use get_renewal_for_user, user levels, coupons, and send renewal_metadata (prev_order_id). New flow (csoc-new-payment or ?level=) uses get_pricing_for_new_user, no user-levels call, no coupons, and sends newcsoc_metadata.
  const {isMobileOrTab} = useMediaQuery();
  const [isLevelSubmitting, setIsLevelSubmitting] = useState(false);
  const [selectedPlanType, setSelectedPlanType] = useState<
    'personal' | 'group' | 'webinar'
  >('personal');
  const [selectedPricingId, setSelectedPricingId] = useState<
    number | undefined
  >(undefined);
  const [selectedLevelSlug, setSelectedLevelSlug] = useState<
    string | undefined
  >(undefined);
  const [selectedDuration, setSelectedDuration] = useState<number>(3);
  const [selectedCoach, setSelectedCoach] = useState<CoachType>('general');
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [isCustomTiming, setIsCustomTiming] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<SelectedBatch | null>(
    null,
  );
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [showAssignmentRequiredPage, setShowAssignmentRequiredPage] =
    useState(false);
  const [assignmentLink, setAssignmentLink] = useState<string>('');
  const [showWebinarDetailsModal, setShowWebinarDetailsModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [orderId, setOrderId] = useState<string>('');
  const [callbackRequested, setCallbackRequested] = useState(false);
  // Billing address state (non-INR payments)
  const [billingFirstName, setBillingFirstName] = useState('');
  const [billingLastName, setBillingLastName] = useState('');
  const [billingLine1, setBillingLine1] = useState('');
  const [billingCountry, setBillingCountry] = useState('');
  const [billingError, setBillingError] = useState('');
  const [mobileCouponCode, setMobileCouponCode] = useState('');
  const [mobileValidationError, setMobileValidationError] = useState<
    string | null
  >(null);
  const {mutate: validateMobileCoupon, isLoading: isCheckingMobileCoupon} =
    useValidateCoupon(
      data => {
        if (data.valid && data.coupon) {
          setMobileValidationError(null);
          setMobileCouponCode('');
          handleApplyCoupon(data.coupon);
        } else {
          setMobileValidationError(data.error || 'Invalid coupon code');
        }
      },
      (error: any) => {
        setMobileValidationError(
          error?.response?.data?.error ||
            error?.message ||
            'Failed to validate coupon',
        );
      },
    );
  // Mobile stage management (1: Plan Selection, 2: Time Slots + Summary, 3: Payment Details)
  const [mobileStage, setMobileStage] = useState<1 | 2 | 3>(1);
  // Refs for scrolling
  const scrollViewRef = useRef<ScrollView>(null);
  const coachSelectorRef = useRef<View>(null);
  const durationSelectorRef = useRef<View>(null);
  const billingMobileSectionRef = useRef<View>(null);
  const billingDesktopSectionRef = useRef<View>(null);

  const [pricingParams, setPricingParams] = useState<{
    level?: string;
    user_level_source?: string;
  }>({});

  // New-user flow: level from URL/params uses get_pricing_for_new_user; otherwise renewal
  const {
    data: renewalDataFromApi,
    isLoading: isLoadingRenewal,
    error: renewalError,
  } = useGetRenewalForUser(!newUser, 'renewal_page');
  const {
    data: newUserPricingData,
    isLoading: isLoadingNewUserPricing,
    error: newUserError,
    refetch: refetchPricingForNewUser,
  } = useGetPricingForNewUser(pricingParams);
  const {
    data: levelsData,
    isLoading: isLoadingLevels,
    error: levelsError,
  } = useGetUserLevels((route as any).name !== 'CsocNewPayment');

  useEffect(() => {
    if (newUserPricingData?.meta?.new_user) {
      if (newUserPricingData?.meta?.user_level) {
        setIsLevelSubmitting(false);
        setLevel(newUserPricingData?.meta?.user_level);
        setShowAssignmentRequiredPage(false);
      } else {
        // Show assignment required page instead of direct redirect
        setAssignmentLink(
          `${newUserPricingData.meta.assignment_link}${
            token?.length ? `&token=${token}` : ''
          }`,
        );
        // (async () => {
        //   const url = new URL(newUserPricingData?.meta?.assignment_link);
        //   const urlParams = new URLSearchParams(url.search);
        //   await setLocalStorage('isEvaluate', true);
        //   await setLocalStorage('tournamentId', urlParams.get('tournamentId'));
        // })();
        setShowAssignmentRequiredPage(true);
        // localStorage.removeItem('oneClickToken');
      }
    } else if (newUserPricingData && newUserPricingData?.meta) {
      setNewUser(false);
      navigation.navigate(ScreenName.Renewal);
    }
  }, [navigation, newUserPricingData, token]);

  const renewalData = level ? newUserPricingData : renewalDataFromApi;
  const isLoading = level
    ? isLoadingNewUserPricing
    : isLoadingRenewal || isLoadingLevels;
  const error = level ? newUserError : renewalError || levelsError;

  const openAlertModal = useAlertModalStore(state => state.openModal);

  // Check if user's plan requires customer support for renewal
  const [showSupportModal, setShowSupportModal] = useState(false);

  // Check if current subscription requires customer support for renewal (skip for new-user flow)
  useEffect(() => {
    if (level || !renewalData?.current_subscription?.tournament_id) {
      return;
    }
    const currentTournamentId = renewalData.current_subscription.tournament_id;
    if (
      (SUPPORT_REQUIRED_TOURNAMENT_IDS as number[]).includes(
        currentTournamentId,
      )
    ) {
      setShowSupportModal(true);
    }
  }, [level, renewalData?.current_subscription?.tournament_id]);

  // Set default plan type and coach based on current subscription or recommended pricing
  // Renewal: use full pricing (no filter). New-user only: auto-select only from displayed options (exclude hidden duet).
  useEffect(() => {
    const pricing = renewalData?.pricing ?? [];
    const isNewUserFlow = Boolean(level);
    const displayedPricing = isNewUserFlow
      ? pricing.filter(
          p =>
            p.format_slug &&
            p.format_slug !== 'duel_group_2_students' &&
            p.level_slug &&
            p.price_per_session &&
            p.price_per_session !== '0',
        )
      : pricing;

    const recommendedPricing = isNewUserFlow
      ? displayedPricing.find(p => p.is_recommended) ?? displayedPricing[0]
      : pricing.find(p => p.is_recommended);

    if (recommendedPricing) {
      setSelectedPlanType(
        recommendedPricing.plan_type as 'personal' | 'group' | 'webinar',
      );
      setSelectedPricingId(recommendedPricing.id);
      if (recommendedPricing.level_slug) {
        setSelectedLevelSlug(recommendedPricing.level_slug);
      }
      const coachTierMap: Record<string, CoachType> = {
        GEN: 'general',
        FM: 'fide_master',
        IM: 'international_master',
        GM: 'grand_master',
      };
      setSelectedCoach(
        coachTierMap[recommendedPricing.coach_tier_code || 'GEN'] || 'general',
      );
    } else if (renewalData?.current_subscription?.plan_type) {
      // Fallback to current subscription (renewal only; new-user has no current_subscription)
      const currentPlanType = renewalData.current_subscription.plan_type;
      setSelectedPlanType(currentPlanType as 'personal' | 'group' | 'webinar');

      let currentCoach: CoachType = 'general';
      if (renewalData?.current_subscription?.coach_type) {
        currentCoach = renewalData.current_subscription.coach_type as CoachType;
        setSelectedCoach(currentCoach);
      } else {
        setSelectedCoach('general');
      }

      const coachTierMap: Record<CoachType, string> = {
        general: 'GEN',
        fide_master: 'FM',
        international_master: 'IM',
        grand_master: 'GM',
      };
      const currentCoachTierCode = coachTierMap[currentCoach] || 'GEN';

      // Match from full pricing for renewal; from displayed only for new-user
      const matchingPricing = displayedPricing.find(
        p =>
          p.plan_type === currentPlanType &&
          p.coach_tier_code === currentCoachTierCode &&
          p.price_per_session &&
          p.price_per_session !== '0',
      );
      const fallbackPricing =
        matchingPricing ||
        displayedPricing.find(
          p =>
            p.plan_type === currentPlanType &&
            p.price_per_session &&
            p.price_per_session !== '0',
        );
      if (fallbackPricing) {
        setSelectedPricingId(fallbackPricing.id);
        if (fallbackPricing.level_slug) {
          setSelectedLevelSlug(fallbackPricing.level_slug);
        }
      }
    } else {
      setSelectedCoach('general');
    }
  }, [renewalData, level]);

  // Hook for auto-applying coupon from route params
  // Note: handleApplyCoupon is defined later, but we reference it in the callback
  const {mutate: validateInitialCoupon} = useValidateCoupon(
    data => {
      if (data.valid && data.coupon) {
        setAppliedCoupon(data.coupon);
      }
    },
    (error: any) => {
      // Silently fail - don't show error for auto-apply
      console.log('Auto-apply coupon validation failed:', error);
    },
  );

  // Remove coupon when selection changes (plan type, duration, or coach)
  useEffect(() => {
    if (appliedCoupon) {
      setAppliedCoupon(null);
    }
  }, [selectedPlanType, selectedDuration, selectedCoach]);

  // Auto-select slots from class_days and class_timing when available
  useEffect(() => {
    if (
      renewalData?.current_subscription?.class_days &&
      renewalData?.current_subscription?.class_timing &&
      (selectedPlanType === 'personal' || selectedPlanType === 'group')
    ) {
      try {
        // Parse weekday numbers (1=Monday, 7=Sunday) and convert to dayjs format
        const classDaysStr = renewalData.current_subscription.class_days;
        const classTimingsStr = renewalData.current_subscription.class_timing;

        const dbWeekdays = classDaysStr
          .split(',')
          .map(d => parseInt(d.trim()))
          .filter(d => !isNaN(d) && d >= 1 && d <= 7);

        // Convert to dayjs weekday format (0=Sunday, 1=Monday, ..., 6=Saturday)
        // DB format: 1=Mon, 2=Tue, ..., 7=Sun
        // dayjs format: 0=Sun, 1=Mon, ..., 6=Sat
        const dayjsWeekdays = dbWeekdays.map(dbDay =>
          dbDay === 7 ? 0 : dbDay,
        );

        // Get dates in next 7 days that match these weekdays
        const today = dayjs();
        const matchingDates: string[] = [];
        for (let i = 0; i < 7; i++) {
          const checkDate = today.add(i, 'day');
          const weekday = checkDate.day(); // dayjs: 0=Sun, 1=Mon, ..., 6=Sat
          if (dayjsWeekdays.includes(weekday)) {
            matchingDates.push(checkDate.format('YYYY-MM-DD'));
          }
        }

        // Parse and convert times from IST to local timezone
        const timeStrings = classTimingsStr
          .split(',')
          .map(t => t.trim())
          .filter(t => t.length > 0);

        const autoSelectedSlots: SelectedSlot[] = [];

        // For each matching date, create slots for each time
        matchingDates.forEach(dateStr => {
          timeStrings.forEach(timeStr => {
            try {
              // Parse time string like "7:00pm" or "11:00am"
              const timeLower = timeStr.toLowerCase();
              const isPM = timeLower.includes('pm');
              const timeWithoutMeridian = timeLower
                .replace(/[ap]m/gi, '')
                .trim();
              const [hourStr, minuteStr = '0'] = timeWithoutMeridian
                .split(':')
                .map(s => s.trim());
              let hour = parseInt(hourStr);
              const minute = parseInt(minuteStr) || 0;

              // Convert to 24-hour format
              if (isPM && hour !== 12) {
                hour += 12;
              } else if (!isPM && hour === 12) {
                hour = 0;
              }

              // Create a UTC date by subtracting IST offset (5h 30m) from IST time
              // IST is UTC+5:30, so to get UTC we subtract 5h30m
              const [year, month, day] = dateStr.split('-').map(Number);
              const utcDate = new Date(
                Date.UTC(year, month - 1, day, hour - 5, minute - 30, 0),
              );

              // Format in user's local timezone
              const userTimeZone =
                Intl.DateTimeFormat().resolvedOptions().timeZone;

              const dateFormatter = new Intl.DateTimeFormat('en-CA', {
                timeZone: userTimeZone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              });

              const timeFormatter = new Intl.DateTimeFormat('en-GB', {
                timeZone: userTimeZone,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
              });

              const localDateStr = dateFormatter.format(utcDate); // YYYY-MM-DD
              const localTime = timeFormatter.format(utcDate); // HH:mm:ss

              autoSelectedSlots.push({
                date: localDateStr,
                time: localTime,
              });
            } catch (error) {
              console.warn(`Error parsing time "${timeStr}":`, error);
            }
          });
        });

        // Set auto-selected slots
        if (autoSelectedSlots.length > 0) {
          setSelectedSlots(autoSelectedSlots);
        }
      } catch (error) {
        console.warn(
          'Error auto-selecting slots from class_days and class_timing:',
          error,
        );
      }
    }
  }, [
    renewalData?.current_subscription?.class_days,
    renewalData?.current_subscription?.class_timing,
    selectedPlanType,
  ]);

  // Handle 2-second loader before showing success page
  useEffect(() => {
    if (isProcessingPayment) {
      const timer = setTimeout(() => {
        setIsProcessingPayment(false);
        setShowSuccessPage(true);
      }, 2000); // 2 seconds

      return () => clearTimeout(timer);
    }
  }, [isProcessingPayment]);

  // Get pricing for selected plan type and coach tier (using new dimension fields)
  // Pricing entries are already sorted with recommended ones at the top from backend
  const selectedPricing = useMemo(() => {
    if (!renewalData?.pricing || renewalData.pricing.length === 0) {
      console.warn('No pricing data available');
      return null;
    }

    // Map selected coach to coach tier code
    const coachTierCodeMap: Record<CoachType, string> = {
      general: 'GEN',
      fide_master: 'FM',
      international_master: 'IM',
      grand_master: 'GM',
    };
    const selectedCoachTierCode = coachTierCodeMap[selectedCoach] || 'GEN';

    // Step 0: If selectedPricingId is set, find exact match by ID
    // BUT only use it if it matches the current coach tier and plan type
    if (selectedPricingId !== undefined) {
      const exactIdMatch = renewalData.pricing.find(
        p => p.id === selectedPricingId,
      );
      if (exactIdMatch) {
        // Check if this pricing entry matches the current coach tier and plan type
        const coachTierMatches =
          exactIdMatch.coach_tier_code === selectedCoachTierCode;
        const planTypeMatches =
          exactIdMatch.plan_type === selectedPlanType ||
          (selectedPlanType === 'personal' &&
            (exactIdMatch.format_slug === 'one_on_one' ||
              exactIdMatch.format_slug === 'duel_group_2_students')) ||
          (selectedPlanType === 'webinar' &&
            exactIdMatch.format_slug === 'webinar') ||
          (selectedPlanType === 'group' &&
            (exactIdMatch.format_slug === 'small_group' ||
              exactIdMatch.format_slug === 'master_pride' ||
              exactIdMatch.format_slug === 'master_pride_plus'));

        if (coachTierMatches && planTypeMatches) {
          console.log(
            'Using pricing entry by ID (matches coach tier):',
            exactIdMatch,
          );
          return exactIdMatch;
        } else {
          console.log(
            'Pricing entry by ID does not match current coach tier, finding new match',
          );
          // Fall through to matching logic below
        }
      } else {
        console.warn('Pricing entry with ID not found:', selectedPricingId);
        // Fall through to existing logic
      }
    }

    // Helper function to check if a pricing entry matches the plan type
    const matchesPlanType = (p: (typeof renewalData.pricing)[0]): boolean => {
      if (p.plan_type === selectedPlanType) {
        return true;
      }
      if (selectedPlanType === 'personal') {
        return (
          p.format_slug === 'one_on_one' ||
          p.format_slug === 'duel_group_2_students'
        );
      }
      if (selectedPlanType === 'webinar') {
        return p.format_slug === 'webinar';
      }
      if (selectedPlanType === 'group') {
        return (
          p.format_slug === 'small_group' ||
          p.format_slug === 'master_pride' ||
          p.format_slug === 'master_pride_plus'
        );
      }
      return false;
    };

    // Helper function to check if a pricing entry matches the level
    const matchesLevel = (p: (typeof renewalData.pricing)[0]): boolean => {
      // If we have a selectedLevelSlug, filter by it
      if (selectedLevelSlug) {
        return p.level_slug === selectedLevelSlug;
      }
      // If no level selected, match any level
      return true;
    };

    console.log('Finding pricing entry:', {
      selectedPlanType,
      selectedCoach,
      selectedCoachTierCode,
      selectedPricingId,
      selectedLevelSlug,
      availablePricing: renewalData.pricing.map(p => ({
        id: p.id,
        plan_type: p.plan_type,
        format_slug: p.format_slug,
        level_slug: p.level_slug,
        coach_tier_code: p.coach_tier_code,
        price_per_session: p.price_per_session,
        is_recommended: p.is_recommended,
      })),
    });

    // Step 1: Try to find exact match (plan type + coach tier + level)
    const exactMatches = renewalData.pricing.filter(p => {
      const coachTierMatches = p.coach_tier_code === selectedCoachTierCode;
      const planTypeMatches = matchesPlanType(p);
      const levelMatches = matchesLevel(p);
      return (
        coachTierMatches &&
        planTypeMatches &&
        levelMatches &&
        p.price_per_session &&
        p.price_per_session !== '0'
      );
    });

    if (exactMatches.length > 0) {
      // Prefer recommended entry if available
      const recommendedMatch = exactMatches.find(p => p.is_recommended);
      if (recommendedMatch) {
        console.log('Using recommended exact match:', recommendedMatch);
        return recommendedMatch;
      }
      console.log('Using first exact match:', exactMatches[0]);
      return exactMatches[0];
    }

    // Step 2: If no exact match, try with GEN coach tier (fallback) but still match level
    if (selectedCoachTierCode !== 'GEN') {
      const genMatches = renewalData.pricing.filter(p => {
        return (
          p.coach_tier_code === 'GEN' &&
          matchesPlanType(p) &&
          matchesLevel(p) &&
          p.price_per_session &&
          p.price_per_session !== '0'
        );
      });

      if (genMatches.length > 0) {
        // Prefer recommended entry if available
        const recommendedGenMatch = genMatches.find(p => p.is_recommended);
        if (recommendedGenMatch) {
          console.log('Using recommended GEN fallback:', recommendedGenMatch);
          return recommendedGenMatch;
        }
        console.log('Using GEN fallback:', genMatches[0]);
        return genMatches[0];
      }
    }

    // Step 3: Last resort - find any matching plan type with any coach tier but still match level
    const anyCoachMatches = renewalData.pricing.filter(p => {
      return (
        matchesPlanType(p) &&
        matchesLevel(p) &&
        p.price_per_session &&
        p.price_per_session !== '0'
      );
    });

    if (anyCoachMatches.length > 0) {
      // Prefer recommended entry if available
      const recommendedAnyMatch = anyCoachMatches.find(p => p.is_recommended);
      if (recommendedAnyMatch) {
        console.log('Using recommended any-coach match:', recommendedAnyMatch);
        return recommendedAnyMatch;
      }
      console.log('Using any-coach match:', anyCoachMatches[0]);
      return anyCoachMatches[0];
    }

    console.warn('No pricing entry found for:', {
      selectedPlanType,
      selectedCoach,
      selectedCoachTierCode,
      selectedPricingId,
      selectedLevelSlug,
    });
    return null;
  }, [
    renewalData,
    selectedPlanType,
    selectedCoach,
    selectedPricingId,
    selectedLevelSlug,
  ]);

  // Auto-apply coupon from route params when component mounts (skip for new payment page)
  useEffect(() => {
    if (
      !initialCouponCode ||
      appliedCoupon ||
      !renewalData ||
      !selectedPricing ||
      !selectedPlanType ||
      !selectedDuration
    ) {
      return;
    }
    const tournamentId =
      selectedPricing.tournament_id ||
      renewalData?.pricing?.find(p => p.is_recommended)?.tournament_id ||
      renewalData?.current_subscription?.tournament_id;

    if (tournamentId) {
      validateInitialCoupon({
        coupon_code: initialCouponCode,
        tournament_id: tournamentId,
        plan_type: selectedPlanType,
        duration_months: selectedDuration,
        source: isNewPaymentPage ? 'checkout_page' : 'renewal_page',
      });
    }
  }, [
    isNewPaymentPage,
    initialCouponCode,
    renewalData,
    selectedPricing,
    selectedPlanType,
    selectedDuration,
    appliedCoupon,
    validateInitialCoupon,
  ]);

  // Debug: Log when selectedPricing changes
  useEffect(() => {
    console.log('Selected pricing changed:', {
      selectedCoach,
      selectedPlanType,
      selectedPricing: selectedPricing
        ? {
            coach_tier_code: selectedPricing.coach_tier_code,
            price_per_session: selectedPricing.price_per_session,
            plan_type: selectedPricing.plan_type,
          }
        : null,
    });
  }, [selectedPricing, selectedCoach, selectedPlanType]);

  // Coach premium multiplier - always 1.0 since backend prices already include coach tier pricing
  const coachPremiumMultiplier = 1.0;

  // Calculate pricing breakdown based on new API structure (price_per_session)
  const pricingBreakdown = useMemo(() => {
    if (!selectedPricing) {
      console.warn('No selected pricing available for breakdown calculation');
      return null;
    }
    const roadmap = selectedPricing.roadmaps?.find(
      r => r.duration_months === selectedDuration,
    );
    if (!roadmap || roadmap.incomplete) {
      console.warn(
        'No complete roadmap for selected duration',
        selectedDuration,
      );
      return null;
    }

    // Prioritize pricing entry's currency_code since that's what the price values are in
    const currency =
      selectedPricing.currency_code ||
      selectedPricing.currency ||
      renewalData?.currency ||
      'INR';

    // Base price = the roadmap's real blended total (each level segment's own
    // MRP-per-session x its own months, summed), NOT a flat single-level price
    // x total-months guess. This matches the same convention the backend's
    // calculate_renewal_pricing and every CRM pricing tool use: roadmap.max_total_price
    // (falling back to total_price) is "base_fee" before duration/coupon discounts.
    // A flat calc silently mis-prices any duration that spans more than one level
    // (Foundation1-4 in particular) -- see the roadmap-pricing audit docs.
    const maxBasePriceInclGst = parseFloat(
      roadmap.max_total_price || roadmap.total_price || '0',
    );

    // Use the roadmap-blended max price as the base price for all calculations
    const basePriceInclGst = maxBasePriceInclGst;
    const gstRate = 0.18; // 18% GST
    const gst =
      currency !== 'INR' ? 0 : basePriceInclGst * (gstRate / (1 + gstRate));

    // Offer price from roadmap (not used in calculations, just for reference)
    const offerPriceInclGst = parseFloat(roadmap.total_price || '0');
    // Offer discount ignored - not used in calculations
    const offerDiscountAmount = 0;
    const offerDiscountPercentage = 0;

    // Get duration discount for selected tournament (applied to max price)
    let durationDiscountPct = 0;
    let durationDiscount = 0;

    if (renewalData?.duration_discounts && selectedPricing?.tournament_id) {
      const tournamentId = selectedPricing.tournament_id.toString();
      const discounts = renewalData.duration_discounts;

      // Try tournament-specific discount first
      if (
        discounts[tournamentId] &&
        discounts[tournamentId][selectedDuration]
      ) {
        durationDiscountPct = discounts[tournamentId][selectedDuration];
      }
      // Fallback to global discount (keyed as 'null' string)
      else if (discounts.null && discounts.null[selectedDuration]) {
        durationDiscountPct = discounts.null[selectedDuration];
      }

      // Calculate discount amount (applied to max price)
      if (durationDiscountPct > 0) {
        durationDiscount = (basePriceInclGst * durationDiscountPct) / 100;
      }
    }

    // Coupon discount (applied to max price)
    let couponDiscount = 0;
    if (appliedCoupon) {
      if (appliedCoupon.discount_type === 'percentage') {
        couponDiscount =
          (basePriceInclGst * parseFloat(appliedCoupon.discount_value)) / 100;
      } else {
        couponDiscount = parseFloat(appliedCoupon.discount_value);
      }
    }

    // Subtotal after discounts (max price - duration discount - coupon discount)
    const subtotalAfterDiscounts =
      basePriceInclGst - durationDiscount - couponDiscount;

    // Grand total
    const grandTotal = subtotalAfterDiscounts;

    // Monthly equivalent
    const monthlyEquivalent = grandTotal / selectedDuration;

    return {
      maxBasePrice: maxBasePriceInclGst, // Max price (from max_price_per_session) - this is the base
      basePrice: basePriceInclGst, // Same as maxBasePrice (max price is the base)
      offerPrice: offerPriceInclGst, // Offer price (hidden from display)
      offerDiscountAmount, // Discount amount from max to offer price
      offerDiscountPercentage, // Discount percentage from max to offer price
      coachPremium: 0, // No frontend premium - price already includes coach tier from backend
      durationDiscount, // Duration-based automatic discount (applied to max price)
      durationDiscountPct, // Duration discount percentage
      couponDiscount, // Coupon discount (applied to max price)
      subtotal: subtotalAfterDiscounts,
      gst,
      grandTotal,
      monthlyEquivalent,
      currency,
    };
  }, [
    selectedPricing,
    appliedCoupon,
    selectedDuration,
    selectedCoach,
    selectedPlanType,
    renewalData?.duration_discounts, // Add this dependency
  ]);

  // Detect a genuinely broken price for the selected plan/duration: either no
  // pricing came back at all, or the roadmap for this duration is incomplete
  // (e.g. a bad next_level_pricing_ids chain on the pricing catalogue row).
  // In that case, don't show a 0 — tell the user to contact support. The
  // backend (get-renewal-for-user) already logs + alerts + raises a CSM/tech
  // ticket for this same condition, so nothing to report from here.
  const selectedRoadmapIncomplete = useMemo(() => {
    if (level) return false; // new-user flow: handled separately, not a renewal block
    if (!renewalData?.current_subscription) return false;
    if (!renewalData.pricing || renewalData.pricing.length === 0) return true;
    if (!selectedPricing) return false; // still resolving, not yet an error state
    const roadmap = selectedPricing.roadmaps?.find(
      r => r.duration_months === selectedDuration,
    );
    return !roadmap || roadmap.incomplete;
  }, [level, renewalData, selectedPricing, selectedDuration]);

  useEffect(() => {
    if (selectedRoadmapIncomplete) {
      setShowSupportModal(true);
    }
  }, [selectedRoadmapIncomplete]);

  const isNonINR =
    pricingBreakdown?.currency && pricingBreakdown.currency !== 'INR';
  const isBillingComplete =
    billingFirstName.trim() &&
    billingLastName.trim() &&
    billingLine1.trim() &&
    billingCountry.trim();
  const billingCountryOptions = useMemo(
    () =>
      [...countryCodes]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(country => ({
          label: `${country.name} (${country.code})`,
          value: country.name,
        })),
    [],
  );
  const selectedBillingCountry =
    billingCountryOptions.find(option => option.value === billingCountry) ||
    null;
  const billingMissingMessage =
    'Billing details are missing. Please fill first name, last name, address and country before proceeding to payment.';

  const clearBillingError = () => {
    if (billingError) {
      setBillingError('');
    }
  };

  const scrollToBillingSection = () => {
    setTimeout(() => {
      if (isMobileOrTab) {
        if (scrollViewRef.current && billingMobileSectionRef.current) {
          billingMobileSectionRef.current.measure(
            (x, y, width, height, pageX, pageY) => {
              scrollViewRef.current?.measure((sx, sy, sw, sh, spx, spy) => {
                const scrollY = pageY - spy - 20;
                scrollViewRef.current?.scrollTo({
                  y: Math.max(0, scrollY),
                  animated: true,
                });
              });
            },
          );
        }
      } else {
        (billingDesktopSectionRef.current as any)?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 300);
  };

  const validateBillingBeforePayment = () => {
    if (isNonINR && !isBillingComplete) {
      setBillingError(billingMissingMessage);
      setShowPaymentModal(false);
      if (isMobileOrTab) {
        setMobileStage(3);
      }
      scrollToBillingSection();
      return false;
    }

    setBillingError('');
    return true;
  };

  // Calculate base price for mobile Next button (show max price).
  // Uses the roadmap's real blended total for that duration, same convention
  // as pricingBreakdown above -- not a flat single-level x months guess.
  const basePriceForButton = useMemo(() => {
    if (mobileStage === 1 && selectedPricing) {
      // For stage 1, use the roadmap for the first available duration
      const defaultDuration =
        selectedDuration || renewalData?.available_durations?.[0] || 3;
      const roadmap = selectedPricing.roadmaps?.find(
        r => r.duration_months === defaultDuration,
      );
      if (!roadmap || roadmap.incomplete) return 0;
      return parseFloat(roadmap.max_total_price || roadmap.total_price || '0');
    }
    if (mobileStage === 2 && selectedDuration && selectedPricing) {
      // For stage 2, use the roadmap for the selected duration
      const roadmap = selectedPricing.roadmaps?.find(
        r => r.duration_months === selectedDuration,
      );
      if (!roadmap || roadmap.incomplete) return 0;
      return parseFloat(roadmap.max_total_price || roadmap.total_price || '0');
    }
    return 0;
  }, [
    mobileStage,
    selectedPricing,
    selectedDuration,
    renewalData?.available_durations,
  ]);

  // Calculate total sessions based on duration
  const totalSessions = useMemo(() => {
    const sessionsPerMonth = 8; // 3 months = 24 sessions, so 8 sessions per month
    return sessionsPerMonth * selectedDuration;
  }, [selectedPlanType, selectedDuration]);

  // Get available coach tiers based on pricing data for the selected plan type AND level
  const availableCoachTiers = useMemo(() => {
    if (!renewalData?.pricing || renewalData.pricing.length === 0) {
      return ['general']; // Default to general if no pricing data
    }

    // Helper function to check if a pricing entry matches the plan type
    const matchesPlanType = (p: (typeof renewalData.pricing)[0]): boolean => {
      if (p.plan_type === selectedPlanType) {
        return true;
      }
      if (selectedPlanType === 'personal') {
        return (
          p.format_slug === 'one_on_one' ||
          p.format_slug === 'duel_group_2_students'
        );
      }
      if (selectedPlanType === 'webinar') {
        return p.format_slug === 'webinar';
      }
      if (selectedPlanType === 'group') {
        return (
          p.format_slug === 'small_group' ||
          p.format_slug === 'master_pride' ||
          p.format_slug === 'master_pride_plus'
        );
      }
      return false;
    };

    // Get the level_slug from selectedPricing if available
    // This ensures we only show coach tiers available for the selected level (e.g., intermediate vs advanced)
    const selectedLevelSlug = selectedPricing?.level_slug;

    // Find all unique coach tier codes that have pricing for this plan type AND level
    const coachTierCodes = new Set<string>();

    renewalData.pricing.forEach(p => {
      // Check if pricing entry matches plan type
      const planTypeMatches = matchesPlanType(p);

      // If we have a selected level, also check if the pricing entry matches that level
      const levelMatches = selectedLevelSlug
        ? p.level_slug === selectedLevelSlug
        : true; // If no level selected, match any level

      if (
        planTypeMatches &&
        levelMatches &&
        p.coach_tier_code &&
        p.price_per_session &&
        p.price_per_session !== '0'
      ) {
        coachTierCodes.add(p.coach_tier_code);
      }
    });

    // Map coach tier codes to CoachType
    const codeToCoachType: Record<string, CoachType> = {
      GEN: 'general',
      FM: 'fide_master',
      IM: 'international_master',
      GM: 'grand_master',
    };

    // Convert to CoachType array, always include 'general' if available
    const availableTiers: CoachType[] = [];
    if (coachTierCodes.has('GEN')) {
      availableTiers.push('general');
    }
    if (coachTierCodes.has('FM')) {
      availableTiers.push('fide_master');
    }
    if (coachTierCodes.has('IM')) {
      availableTiers.push('international_master');
    }
    if (coachTierCodes.has('GM')) {
      availableTiers.push('grand_master');
    }

    // Always include general as fallback if no other options
    if (availableTiers.length === 0) {
      availableTiers.push('general');
    }

    console.log(
      'Available coach tiers for plan type:',
      selectedPlanType,
      'level:',
      selectedLevelSlug,
      availableTiers,
    );
    return availableTiers;
  }, [renewalData, selectedPlanType, selectedPricing]);

  // Get program name from levels data or recommended pricing entry
  const programName = useMemo(() => {
    if (levelsData?.levels) {
      const currentLevel = levelsData.levels.find(level => level.isCurrent);
      const nextLevel = levelsData.levels.find(
        level => level.order === (currentLevel?.order || 0) + 1,
      );
      if (nextLevel?.name) {
        return nextLevel.name;
      }
      if (currentLevel?.name) {
        return currentLevel.name;
      }
    }
    // Fallback to recommended pricing entry tournament name
    const recommendedPricing = renewalData?.pricing?.find(
      p => p.is_recommended,
    );
    if (recommendedPricing?.tournament_name) {
      return recommendedPricing.tournament_name;
    }
    return 'Next Level';
  }, [levelsData, renewalData]);

  // Map API color strings to CCColors
  const mapColor = (colorString: string): string => {
    switch (colorString) {
      case 'green':
        return CCColors.Green;
      case 'primary':
        return CCColors.Primary.Default;
      case 'grey':
        return CCColors.Grey.Grey;
      case 'dark':
        return CCColors.TextColor.Dark;
      default:
        return CCColors.Grey.Grey;
    }
  };

  // Level progression data from API - filter to show only current and next level
  const levels: Level[] = useMemo(() => {
    if (!levelsData?.levels) {
      return [];
    }
    const allLevels = levelsData.levels.map(level => ({
      name: level.name,
      order: level.order,
      isCurrent: level.isCurrent,
      isCompleted: level.isCompleted,
      color: mapColor(level.color),
      isLocked: level.isLocked ?? false,
    }));

    // Find current level
    const currentLevel = allLevels.find(level => level.isCurrent);
    if (!currentLevel) {
      return allLevels.slice(0, 2); // Fallback: show first 2 levels
    }

    // Find next level (lowest order that is greater than current and not locked)
    const nextLevel = allLevels
      .filter(level => level.order > currentLevel.order && !level.isLocked)
      .sort((a, b) => a.order - b.order)[0];

    // Return only current and next level
    if (nextLevel) {
      return [currentLevel, nextLevel];
    }

    // If no next level found, just return current level
    return [currentLevel];
  }, [levelsData]);

  // Generate pricing options for available durations from API based on price_per_session
  const pricingOptions = useMemo(() => {
    if (!renewalData?.pricing || renewalData.pricing.length === 0) {
      return [];
    }

    // Use selectedPricing (already matches plan/coach via upstream selection)
    if (!selectedPricing) {
      console.warn('No selectedPricing for pricingOptions');
      return [];
    }

    // Prioritize pricing entry's currency_code since that's what the price values are in
    const currency =
      selectedPricing.currency_code ||
      selectedPricing.currency ||
      renewalData?.currency ||
      'INR';
    const availableDurations = renewalData.available_durations || [3, 6, 12];

    const options = availableDurations
      .map(duration => {
        const roadmap = selectedPricing.roadmaps?.find(
          r => r.duration_months === duration,
        );
        if (!roadmap || roadmap.incomplete) {
          console.warn(
            'No complete roadmap for duration',
            duration,
            'pricing id',
            selectedPricing.id,
          );
          return null;
        }
        const totalPrice = parseFloat(roadmap.total_price || '0');
        const monthlyPrice = totalPrice / duration;
        return {
          ...selectedPricing,
          duration_months: duration,
          per_month_price: monthlyPrice.toFixed(2),
          base_price: totalPrice.toFixed(2),
          currency,
          roadmap,
        };
      })
      .filter(Boolean) as any[];

    return options;
  }, [renewalData, selectedPlanType, selectedCoach, selectedPricing]);

  // Extract available plan types from pricing array
  const availablePlanTypes = useMemo(() => {
    if (!renewalData?.pricing || renewalData.pricing.length === 0) {
      return [];
    }
    // Get unique plan types from pricing array
    const planTypes = new Set<string>();
    renewalData.pricing.forEach(p => {
      if (p.plan_type) {
        planTypes.add(p.plan_type);
      }
    });
    return Array.from(planTypes) as ('personal' | 'group' | 'webinar')[];
  }, [renewalData]);

  // Group pricing entries by level (not coach) to create plan options
  // If entries differ only by coach_tier, show as one option (coach selection in CoachSelector)
  // If entries differ by level, show multiple options with level names
  const availablePlanOptions = useMemo(() => {
    if (!renewalData?.pricing || renewalData.pricing.length === 0) {
      return [];
    }

    // Helper to map format_slug to plan type
    const formatToPlanType = (
      formatSlug: string,
    ): 'personal' | 'group' | 'webinar' | null => {
      if (
        formatSlug === 'one_on_one' ||
        formatSlug === 'duel_group_2_students'
      ) {
        return 'personal';
      }
      if (formatSlug === 'webinar') {
        return 'webinar';
      }
      if (
        formatSlug === 'small_group' ||
        formatSlug === 'master_pride' ||
        formatSlug === 'master_pride_plus'
      ) {
        return 'group';
      }
      return null;
    };

    // Group entries by (format_slug, level_slug) combination
    // Entries with same format+level but different coach_tier will be grouped together
    const groupedByFormatAndLevel = new Map<
      string,
      Array<(typeof renewalData.pricing)[0]>
    >();

    renewalData.pricing.forEach(p => {
      if (
        !p.format_slug ||
        !p.level_slug ||
        !p.price_per_session ||
        p.price_per_session === '0'
      ) {
        return; // Skip invalid entries
      }
      // Hide personal-duet (Personal Duel Group) from plan selector UI for new-user flow only; renewal sees all plans
      if (level && p.format_slug === 'duel_group_2_students') {
        return;
      }

      const planType = formatToPlanType(p.format_slug);
      if (!planType) {
        return;
      } // Skip unknown formats

      // Create key: format_slug + level_slug (coach_tier is ignored for grouping)
      const key = `${p.format_slug}|${p.level_slug}`;

      if (!groupedByFormatAndLevel.has(key)) {
        groupedByFormatAndLevel.set(key, []);
      }
      groupedByFormatAndLevel.get(key)!.push(p);
    });

    // For each unique (format_slug, level_slug) combination, pick one representative entry
    // Prefer recommended, then first entry
    const options: Array<{
      type: 'personal' | 'group' | 'webinar';
      level_name: string;
      level_slug: string;
      pricing_id: number;
      format_slug: string;
      format_name?: string;
      is_recommended?: boolean;
    }> = [];

    groupedByFormatAndLevel.forEach((entries, key) => {
      // Find recommended entry first, otherwise use first entry
      const recommendedEntry = entries.find(e => e.is_recommended);
      const representativeEntry = recommendedEntry || entries[0];

      const planType = formatToPlanType(representativeEntry.format_slug!);
      if (!planType) {
        return;
      }

      options.push({
        type: planType,
        level_name:
          representativeEntry.level_name ||
          representativeEntry.level_slug ||
          '',
        level_slug: representativeEntry.level_slug || '',
        pricing_id: representativeEntry.id,
        format_slug: representativeEntry.format_slug!,
        format_name: representativeEntry.format_name,
        is_recommended: representativeEntry.is_recommended,
      });
    });

    // Sort: recommended first, then by level order if available
    options.sort((a, b) => {
      if (a.is_recommended && !b.is_recommended) {
        return -1;
      }
      if (!a.is_recommended && b.is_recommended) {
        return 1;
      }
      // Could add level ordering here if needed
      return 0;
    });

    return options;
  }, [renewalData, level]);

  const handlePlanTypeSelect = (
    type: 'personal' | 'group' | 'webinar',
    pricingId?: number,
    levelName?: string,
  ) => {
    // Clear custom time slots when switching to or from personal/group plans
    if (
      type === 'personal' ||
      type === 'group' ||
      selectedPlanType === 'personal' ||
      selectedPlanType === 'group'
    ) {
      setSelectedSlots([]);
    }

    setSelectedPlanType(type);
    setSelectedPricingId(pricingId);

    // Find and store the level_slug for the selected pricing
    if (pricingId && renewalData?.pricing) {
      const pricingEntry = renewalData.pricing.find(p => p.id === pricingId);
      if (pricingEntry?.level_slug) {
        setSelectedLevelSlug(pricingEntry.level_slug);
      }
    } else {
      setSelectedLevelSlug(undefined);
    }

    // Show slot modal for webinar plans only (small_group handled via CTA)
    // if (type === 'webinar') {
    //   setShowSlotModal(true);
    // }
    // Note: For personal and small_group, slots are optional and set via CTA/button

    // Scroll to coach selector after a short delay to ensure it's rendered
    setTimeout(() => {
      if (scrollViewRef.current && coachSelectorRef.current) {
        coachSelectorRef.current.measure(
          (x, y, width, height, pageX, pageY) => {
            // Get ScrollView position
            scrollViewRef.current?.measure((sx, sy, sw, sh, spx, spy) => {
              const scrollY = pageY - spy - 20; // Offset for better visibility
              scrollViewRef.current?.scrollTo({
                y: Math.max(0, scrollY),
                animated: true,
              });
            });
          },
        );
      }
    }, 300);
  };

  const handleCoachSelect = (coach: CoachType) => {
    // Allow deselecting by clicking the same coach again (toggle behavior)
    // If clicking the same coach, reset to 'general'
    const newCoach =
      selectedCoach === coach && coach !== 'general' ? 'general' : coach;
    setSelectedCoach(newCoach);

    // Reset selectedPricingId when coach changes so that selectedPricing logic
    // can find the correct pricing entry for the new coach tier
    // BUT preserve selectedLevelSlug to maintain the same level
    setSelectedPricingId(undefined);
    // Don't reset selectedLevelSlug - we want to keep the same level

    // Scroll to duration selector after a short delay
    setTimeout(() => {
      if (scrollViewRef.current && durationSelectorRef.current) {
        durationSelectorRef.current.measure(
          (x, y, width, height, pageX, pageY) => {
            // Get ScrollView position
            scrollViewRef.current?.measure((sx, sy, sw, sh, spx, spy) => {
              const scrollY = pageY - spy - 20; // Offset for better visibility
              scrollViewRef.current?.scrollTo({
                y: Math.max(0, scrollY),
                animated: true,
              });
            });
          },
        );
      }
    }, 300);
  };

  const handleDurationSelect = (duration: number) => {
    setSelectedDuration(duration);
  };

  const handleSlotSelect = (slots: SelectedSlot[]) => {
    setSelectedSlots(slots);
    setShowSlotModal(false);
  };

  const handleRemoveSlot = (index: number) => {
    setSelectedSlots(selectedSlots.filter((_, i) => i !== index));
  };

  const handleEditSlots = () => {
    setIsCustomTiming(false);
    setShowSlotModal(true);
  };

  const handleSelectCustomTiming = () => {
    const isFoundation =
      newUserPricingData?.meta?.is_user_preference_foundation;
    // For group plan type, show batch selection modal
    // For personal/webinar, show slot selection modal
    if (selectedPlanType === 'group') {
      if (isFoundation) {
        setIsCustomTiming(true);
        setShowSlotModal(true);
        return;
      }
      setShowBatchModal(true);
      return;
    } else {
      setIsCustomTiming(true);
      setShowSlotModal(true);
    }
  };

  const handleBatchSelect = (batch: SelectedBatch | null) => {
    setSelectedBatch(batch);
    setShowBatchModal(false);
  };

  const handleOpenCustomTimingFromBatch = () => {
    // Close batch modal and open slot modal
    const isFoundation =
      newUserPricingData?.meta?.is_user_preference_foundation;

    if (isFoundation) {
      setIsCustomTiming(true);
      setShowSlotModal(true);
      return;
    }

    setShowBatchModal(false);
    setIsCustomTiming(true);
    setShowSlotModal(true);
  };

  const handleApplyCoupon = (coupon: any) => {
    setAppliedCoupon(coupon);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
  };

  const handleCheckMobileCoupon = () => {
    if (!mobileCouponCode.trim()) {
      setMobileValidationError('Please enter a coupon code');
      return;
    }
    if (!selectedPricing?.tournament_id && !renewalData?.current_subscription) {
      setMobileValidationError('Plan details missing');
      return;
    }
    setMobileValidationError(null);
    validateMobileCoupon({
      coupon_code: mobileCouponCode.trim(),
      tournament_id:
        selectedPricing?.tournament_id ||
        renewalData?.current_subscription?.tournament_id,
      plan_type: selectedPlanType,
      duration_months: selectedDuration,
      source: isNewPaymentPage ? 'checkout_page' : 'renewal_page',
    });
  };

  const handlePayNow = () => {
    const isFoundation =
      newUserPricingData?.meta?.is_user_preference_foundation;
    // Validate: For group plan type, either batch or custom slots must be selected
    if (selectedPlanType === 'group') {
      if (!isFoundation) {
        if (!selectedBatch && selectedSlots.length === 0) {
          // Open batch selection modal instead of showing error
          setShowBatchModal(true);
          // Show info message that selection is mandatory
          // setTimeout(() => {
          //   openAlertModal({
          //     header: 'Selection Required',
          //     bodyTxt: 'Please select a batch or choose custom date/time slots. This is mandatory to proceed with payment.',
          //     buttonText: 'OK',
          //   });
          // }, 300); // Small delay to let modal open first
          return;
        }
      }
    }

    if (!validateBillingBeforePayment()) {
      return;
    }

    if (isMobileOrTab) {
      // On mobile, navigate to payment stage
      setMobileStage(3);
    } else {
      // On desktop, show payment modal
      setShowPaymentModal(true);
    }
  };

  // Mobile stage navigation handlers
  const handleNextStage = () => {
    if (mobileStage < 3) {
      setMobileStage(prev => (prev + 1) as 1 | 2 | 3);
      // Scroll to top when changing stages
      scrollViewRef.current?.scrollTo({y: 0, animated: true});
    }
  };

  const handleBackStage = () => {
    if (mobileStage > 1) {
      setMobileStage(prev => (prev - 1) as 1 | 2 | 3);
      // Scroll to top when changing stages
      scrollViewRef.current?.scrollTo({y: 0, animated: true});
    }
  };

  const handleMobilePay = () => {
    const isFoundation =
      newUserPricingData?.meta?.is_user_preference_foundation;
    // Validate: For group plan type, either batch or custom slots must be selected
    if (selectedPlanType === 'group') {
      if (!isFoundation) {
        if (!selectedBatch && selectedSlots.length === 0) {
          // Open batch selection modal instead of showing error
          setShowBatchModal(true);
          // Show info message that selection is mandatory
          // setTimeout(() => {
          //   openAlertModal({
          //     header: 'Selection Required',
          //     bodyTxt: 'Please select a batch or choose custom date/time slots. This is mandatory to proceed with payment.',
          //     buttonText: 'OK',
          //   });
          // }, 300); // Small delay to let modal open first
          return;
        }
      }
    }

    if (!validateBillingBeforePayment()) {
      return;
    }

    // On mobile stage 3, show payment confirmation modal
    setShowPaymentModal(true);
  };

  // Request callback hook
  const {mutate: requestCallback, isLoading: isRequestingCallback} =
    useRequestCallback(
      data => {
        if (data.success) {
          setCallbackRequested(true);
          openAlertModal({
            header: 'Callback Requested',
            bodyTxt:
              'Your callback request has been submitted. Our team will contact you shortly.',
            buttonText: 'OK',
          });
        } else {
          openAlertModal({
            header: 'Error',
            bodyTxt:
              data.message ||
              'Failed to submit callback request. Please try again.',
            buttonText: 'OK',
          });
        }
      },
      (error: any) => {
        Alert.alert(
          'Error',
          'Failed to submit callback request. Please try again.',
        );
        console.error('Callback request error:', error);
      },
    );

  const handleRequestCallback = () => {
    if (!selectedPlanType || !selectedDuration) {
      openAlertModal({
        header: 'Error',
        bodyTxt:
          'Please select a plan type and duration before requesting a callback.',
        buttonText: 'OK',
      });
      // Alert.alert('Error', 'Please select a plan type and duration before requesting a callback.');
      return;
    }

    // Map selected coach to coach tier code
    const coachTierCodeMap: Record<CoachType, string> = {
      general: 'GEN',
      fide_master: 'FM',
      international_master: 'IM',
      grand_master: 'GM',
    };
    const selectedCoachTierCode = coachTierCodeMap[selectedCoach] || 'GEN';
    const source =
      (route as any)?.name === ScreenName?.CsocNewPayment
        ? 'checkout_page'
        : 'renewal';

    requestCallback({
      selectedPlanType,
      selectedDuration,
      selectedCoach: selectedCoachTierCode,
      selectedSlots: selectedSlots.length > 0 ? selectedSlots : undefined,
      tournamentId:
        selectedPricing?.tournament_id ||
        renewalData?.current_subscription?.tournament_id,
      source,
      selectedPricing: selectedPricing
        ? {
            level_slug: selectedPricing.level_slug,
            level_name: selectedPricing.level_name,
            format_slug: selectedPricing.format_slug,
            format_name: selectedPricing.format_name,
            coach_tier_code: selectedPricing.coach_tier_code,
          }
        : undefined,
    });
  };

  // Show payment processing loader
  if (isProcessingPayment) {
    return (
      <Box
        flex={1}
        justifyContent="center"
        alignItems="center"
        backgroundColor={CCColors.CCTableBg}>
        <CCLoader loading={true} />
        <CCText
          fontSize={FontSizes[18]}
          color={CCColors.TextColor.Dark}
          marginTop={20}
          textAlign="center">
          Processing your payment...
        </CCText>
      </Box>
    );
  }

  if (isLevelSubmitting) {
    return (
      <Box flex={1} justifyContent="center" alignItems="center">
        <CCLoader loading bgWhite />
      </Box>
    );
  }

  // Show assignment required page if user level is not known
  if (showAssignmentRequiredPage) {
    return (
      // <AssignmentRequiredPage
      //   assignmentLink={assignmentLink}
      //   redirectSeconds={10}
      // />
      <UserLevelRequiredPage
        userId={userData?.user_id}
        refetchPricing={params => {
          setLevel(params.level);
          setPricingParams(params);
        }}
        setSubmitting={setIsLevelSubmitting}
      />
    );
  }

  // Show success page if payment is successful
  if (showSuccessPage) {
    // Use pricing breakdown from useMemo
    // Use pricingBreakdown if available, otherwise show warning
    const breakdown = pricingBreakdown;
    if (!breakdown) {
      console.error(
        'Pricing breakdown is null - cannot display prices. Selected pricing:',
        selectedPricing,
      );
    }

    return (
      <SuccessPage
        orderId={
          orderId ||
          `CSOC-${dayjs().format('YYYY-MM-DD')}-${Math.floor(
            Math.random() * 1000000,
          )}`
        }
        amountPaid={breakdown?.grandTotal || 0}
        currency={breakdown?.currency || renewalData?.currency || 'INR'}
        programName={programName}
        planType={selectedPlanType}
        duration={selectedDuration}
        sessions={totalSessions}
        selectedCoach={selectedCoach}
        firstClassDate={
          selectedSlots.length > 0
            ? dayjs(selectedSlots[0].date).format('dddd, MMM D, YYYY')
            : undefined
        }
        firstClassTime={
          selectedSlots.length > 0 ? selectedSlots[0].time : undefined
        }
        coachName="Sarah Johnson" // TODO: Get from API
        selectedBatch={selectedBatch}
        onViewSchedule={() => {
          // TODO: Navigate to schedule view
          console.log('View Schedule');
        }}
        onDownloadInvoice={() => {
          // TODO: Download invoice
          console.log('Download Invoice');
        }}
      />
    );
  }

  // if (isNewPaymentPage && !level) {
  //   return (
  //     <Box flex={1} justifyContent="center" alignItems="center" padding={20}>
  //       <CCText fontSize={FontSizes[16]} color={CCColors.TextColor.Dark} textAlign="center">
  //         Please open this page with a level. Example: csoc-new-payment?level=beginner
  //       </CCText>
  //     </Box>
  //   );
  // }

  if (isLoading) {
    return (
      <Box flex={1} justifyContent="center" alignItems="center">
        <CCLoader loading={isLoading} />
      </Box>
    );
  }

  if (error || !renewalData) {
    return (
      <Box flex={1} justifyContent="center" alignItems="center" padding={20}>
        <CCText fontSize={FontSizes[16]} color={CCColors.Red}>
          {error?.message || 'Failed to load renewal data'}
        </CCText>
      </Box>
    );
  }

  // Use pricing breakdown from useMemo
  const breakdown = pricingBreakdown;
  if (!breakdown) {
    console.error(
      'Pricing breakdown is null - cannot display prices. Selected pricing:',
      selectedPricing,
    );
    console.error(
      'Available pricing entries:',
      renewalData?.pricing?.map(p => ({
        plan_type: p.plan_type,
        format_slug: p.format_slug,
        coach_tier_code: p.coach_tier_code,
        price_per_session: p.price_per_session,
        is_recommended: p.is_recommended,
      })),
    );
  }

  return (
    <Box paddingRight={10} flex={1} backgroundColor={CCColors.CCTableBg}>
      {/* <RenewalHeader isMobile={isMobileOrTab} /> */}

      {isMobileOrTab ? (
        <View style={{flex: 1}}>
          <ScrollView
            ref={scrollViewRef}
            style={styles.container}
            contentContainerStyle={styles.contentContainer}>
            <Box paddingHorizontal={15} paddingTop={20}>
              {/* Stage Indicator */}
              {!isNewPaymentPage && (
                <Box
                  flexDirection="row"
                  justifyContent="center"
                  alignItems="center"
                  marginBottom={20}
                  gap={8}>
                  {[1, 2, 3].map(stage => (
                    <React.Fragment key={stage}>
                      <Pressable
                        onPress={() => {
                          if (stage <= mobileStage) {
                            setMobileStage(stage as 1 | 2 | 3);
                            scrollViewRef.current?.scrollTo({
                              y: 0,
                              animated: true,
                            });
                          }
                        }}
                        disabled={stage > mobileStage}>
                        <Box
                          width={32}
                          height={32}
                          borderRadius={16}
                          backgroundColor={
                            mobileStage >= stage
                              ? CCColors.Primary.Default
                              : CCColors.Grey.Divider200
                          }
                          justifyContent="center"
                          alignItems="center"
                          opacity={stage > mobileStage ? 0.5 : 1}>
                          <CCText
                            fontSize={FontSizes[14]}
                            fontWeight="bold"
                            color={
                              mobileStage >= stage
                                ? CCColors.White
                                : CCColors.TextColor.Grey
                            }>
                            {stage}
                          </CCText>
                        </Box>
                      </Pressable>
                      {stage < 3 && (
                        <Box
                          width={40}
                          height={2}
                          backgroundColor={
                            mobileStage > stage
                              ? CCColors.Primary.Default
                              : CCColors.Grey.Divider200
                          }
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              )}

              {/* Stage 1: Plan and Coach Selection */}
              {mobileStage === 1 && (
                <>
                  <Box flexDirection="row" marginBottom={24}>
                    {!level && (
                      <CCText
                        fontSize={FontSizes[24]}
                        fontWeight="bold"
                        color={CCColors.TextColor.Dark}>
                        {level ? '' : 'Choose'} Your Plan
                      </CCText>
                    )}
                    {level && (
                      <Box
                        flexDirection="row"
                        alignItems="center"
                        style={{
                          backgroundColor: '#FDF1C8',
                          paddingHorizontal: 12,
                          paddingVertical: 6,
                          borderRadius: 20,
                          alignSelf: 'flex-start',
                          marginTop: 8,
                          borderWidth: 1,
                          borderColor: '#FDE394',
                        }}
                        gap={6}>
                        <MaterialCommunityIcons
                          name="gauge"
                          size={16}
                          color={CCColors.Primary.Brown}
                        />
                        <CCText
                          fontSize={FontSizes[14]}
                          fontWeight="bold"
                          color={CCColors.Primary.Brown}>
                          Level:{' '}
                          {(level &&
                          newUserPricingData?.meta
                            ?.is_user_preference_foundation
                            ? 'foundation'
                            : level
                          )
                            ?.charAt(0)
                            ?.toUpperCase() +
                            (level &&
                            newUserPricingData?.meta
                              ?.is_user_preference_foundation
                              ? 'foundation'
                              : level
                            )?.slice(1)}
                        </CCText>
                      </Box>
                    )}
                  </Box>

                  {/* Level Tracker (hidden for new-user flow) */}
                  {!level && levels.length > 0 ? (
                    <Box marginBottom={30}>
                      <LevelTracker levels={levels} />
                    </Box>
                  ) : !level ? (
                    <Box paddingVertical={20} marginBottom={30}>
                      <CCText
                        fontSize={FontSizes[14]}
                        color={CCColors.TextColor.Grey}
                        textAlign="center">
                        Loading levels...
                      </CCText>
                    </Box>
                  ) : null}

                  <Box marginBottom={30}>
                    <PlanSelector
                      selectedPlan={selectedPlanType}
                      selectedPricingId={selectedPricingId}
                      selectedLevelSlug={selectedLevelSlug}
                      onSelect={handlePlanTypeSelect}
                      onSelectTiming={handleSelectCustomTiming}
                      selectedBatch={selectedBatch}
                      onViewWebinarDetails={() =>
                        setShowWebinarDetailsModal(true)
                      }
                      isMobile={isMobileOrTab}
                      planPerks={selectedPricing?.plan_perks || []}
                      recommendedPlan={
                        renewalData?.pricing?.find(p => p.is_recommended)
                          ?.plan_type as
                          | 'personal'
                          | 'group'
                          | 'webinar'
                          | undefined
                      }
                      showCurrentTag={!isNewPaymentPage}
                      prioritizeSelectedAtTop={isNewPaymentPage}
                      availablePlanTypes={availablePlanTypes}
                      availablePlanOptions={availablePlanOptions}
                      webinarOptions={
                        selectedPlanType === 'webinar' &&
                        selectedPricing?.webinar_options
                          ? selectedPricing.webinar_options
                          : []
                      }
                      selectedBatch={selectedBatch}
                      isFoundationPreference={
                        newUserPricingData?.meta?.is_user_preference_foundation
                      }
                    />

                    {/* Coach Selection */}
                    <View ref={coachSelectorRef}>
                      <CoachSelector
                        selectedCoach={selectedCoach}
                        onSelect={handleCoachSelect}
                        isMobile={isMobileOrTab}
                        premiumAmount={pricingBreakdown?.coachPremium || 0}
                        currency={
                          pricingBreakdown?.currency ||
                          renewalData?.currency ||
                          'INR'
                        }
                        currentCoach={
                          (renewalData?.current_subscription
                            ?.coach_type as CoachType) || 'general'
                        }
                        coachPerks={selectedPricing?.coach_perks || []}
                        availableCoachTiers={availableCoachTiers}
                      />
                    </View>
                  </Box>
                </>
              )}

              {/* Stage 2: Duration Selection */}
              {mobileStage === 2 && (
                <>
                  <CCText
                    fontSize={FontSizes[24]}
                    fontWeight="bold"
                    color={CCColors.TextColor.Dark}
                    marginBottom={20}>
                    Select Duration
                  </CCText>

                  <Box marginBottom={30}>
                    {/* <CCText
                      fontSize={FontSizes[18]}
                      fontWeight="600"
                      color={CCColors.TextColor.Dark}
                      marginBottom={16}>
                      Choose Your Duration
                    </CCText> */}

                    <View ref={durationSelectorRef}>
                      <DurationSelector
                        selectedDuration={selectedDuration}
                        onSelect={handleDurationSelect}
                        pricingOptions={pricingOptions}
                        isMobile={isMobileOrTab}
                        selectedCoach={selectedCoach}
                        availableDurations={renewalData?.available_durations}
                        durationDiscounts={renewalData?.duration_discounts}
                        tournamentId={selectedPricing?.tournament_id}
                      />
                    </View>

                    {/* Time Slot Selection */}
                    {/* <Box marginTop={30}>
              <CCText
                fontSize={FontSizes[18]}
                fontWeight="600"
                color={CCColors.TextColor.Dark}
                marginBottom={16}>
                        Select Time Slots (Optional)
              </CCText>

                      <Box
                        padding={16}
                        backgroundColor={CCColors.White}
                        borderRadius={12}
                        borderWidth={1}
                        borderColor={CCColors.Grey.Divider200}>
                        <Pressable onPress={() => setShowSlotModal(true)}>
                          <Box
                            flexDirection="row"
                            justifyContent="space-between"
                            alignItems="center">
                            <Box flex={1}>
                              <CCText
                                fontSize={FontSizes[16]}
                                fontWeight="600"
                                color={CCColors.TextColor.Dark}
                                marginBottom={4}>
                                {selectedSlots.length > 0
                                  ? `${selectedSlots.length} Slot${
                                      selectedSlots.length > 1 ? 's' : ''
                                    } Selected`
                                  : 'Select Time Slots'}
                              </CCText>
                              {selectedSlots.length === 0 && (
                                <CCText
                                  fontSize={FontSizes[14]}
                                  color={CCColors.TextColor.Grey}>
                                  Tap to choose your preferred class times
                                </CCText>
                              )}
                            </Box>
                            <MaterialCommunityIcons
                              name="chevron-right"
                              size={24}
                              color={CCColors.TextColor.Grey}
              />
            </Box>
                        </Pressable>

                        {selectedSlots.length > 0 && (
                          <Box
                            marginTop={16}
                            paddingTop={16}
                            borderTopWidth={1}
                            borderTopColor={CCColors.Grey.Divider200}>
                            {selectedSlots.map((slot, index) => {
                              const slotDate = dayjs(slot.date);
                              const dayName = slotDate.format('ddd');
                              const timeStr = slot.time.includes('T')
                                ? dayjs(slot.time).format('h:mm A')
                                : dayjs(`2000-01-01 ${slot.time}`).format(
                                    'h:mm A',
                                  );
                              return (
                                <Box
                                  key={`${slot.date}_${slot.time}_${index}`}
                                  flexDirection="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  marginBottom={
                                    index < selectedSlots.length - 1 ? 12 : 0
                                  }>
                                  <Box
                                    flexDirection="row"
                                    alignItems="center"
                                    gap={8}>
                                    <MaterialCommunityIcons
                                      name="calendar-clock"
                                      size={20}
                                      color={CCColors.Primary.Default}
                                    />
                                    <CCText
                                      fontSize={FontSizes[14]}
                                      color={CCColors.TextColor.Dark}>
                                      {dayName}, {slotDate.format('MMM D')} at{' '}
                                      {timeStr}
                                    </CCText>
          </Box>
                                  <Pressable
                                    onPress={() => handleRemoveSlot(index)}>
                                    <MaterialCommunityIcons
                                      name="close-circle"
                                      size={20}
                                      color={CCColors.Red}
                                    />
                                  </Pressable>
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    </Box> */}
                  </Box>
                </>
              )}

              {/* Stage 3: Payment Details */}
              {mobileStage === 3 && (
                <>
                  <CCText
                    fontSize={FontSizes[24]}
                    fontWeight="bold"
                    color={CCColors.TextColor.Dark}
                    marginBottom={20}>
                    Payment Details
                  </CCText>

                  <Box marginBottom={30}>
                    <Box
                      padding={16}
                      backgroundColor={CCColors.White}
                      borderRadius={12}
                      borderWidth={1}
                      borderColor={CCColors.Grey.Divider200}
                      marginBottom={20}>
                      <Box
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        marginBottom={12}>
                        <CCText
                          fontSize={FontSizes[18]}
                          fontWeight="600"
                          color={CCColors.TextColor.Dark}>
                          Coupon Code
                        </CCText>
                        <Pressable onPress={() => setShowCouponModal(true)}>
                          <Box flexDirection="row" alignItems="center" gap={6}>
                            <MaterialCommunityIcons
                              name="ticket-percent"
                              size={18}
                              color={CCColors.Primary.Default}
                            />
                            <CCText
                              fontSize={FontSizes[14]}
                              color={CCColors.Primary.Default}
                              fontWeight="semibold">
                              View
                            </CCText>
                          </Box>
                        </Pressable>
                      </Box>

                      {appliedCoupon ? (
                        <Box
                          flexDirection="row"
                          alignItems="center"
                          justifyContent="space-between"
                          padding={12}
                          backgroundColor={CCColors.Primary.lightBrown}
                          borderRadius={8}
                          borderWidth={1}
                          borderColor={CCColors.Green}>
                          <Box flexDirection="row" alignItems="center" gap={8}>
                            <MaterialCommunityIcons
                              name="check-circle"
                              size={18}
                              color={CCColors.Green}
                            />
                            <CCText
                              fontSize={FontSizes[14]}
                              fontWeight="semibold"
                              color={CCColors.TextColor.Dark}>
                              {appliedCoupon.code}
                            </CCText>
                          </Box>
                          <Pressable
                            onPress={handleRemoveCoupon}
                            hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}
                            style={{padding: 4}}>
                            <MaterialCommunityIcons
                              name="close-circle"
                              size={20}
                              color={CCColors.Red}
                            />
                          </Pressable>
                        </Box>
                      ) : (
                        <Box
                          flexDirection="row"
                          alignItems="center"
                          borderWidth={1}
                          borderColor={CCColors.Grey.Divider200}
                          borderRadius={8}
                          paddingHorizontal={10}
                          height={48}>
                          <TextInput
                            style={styles.couponInput}
                            placeholder="Enter code"
                            placeholderTextColor={CCColors.TextColor.Grey}
                            value={mobileCouponCode}
                            onChangeText={text => {
                              setMobileCouponCode(text);
                              setMobileValidationError(null);
                            }}
                          />
                          <Pressable
                            style={[
                              styles.couponCheckButton,
                              (isCheckingMobileCoupon ||
                                !mobileCouponCode.trim()) &&
                                styles.couponCheckButtonDisabled,
                            ]}
                            onPress={handleCheckMobileCoupon}
                            disabled={
                              isCheckingMobileCoupon || !mobileCouponCode.trim()
                            }>
                            <Box
                              flexDirection="row"
                              alignItems="center"
                              justifyContent="center"
                              gap={6}>
                              {isCheckingMobileCoupon && (
                                <ActivityIndicator
                                  size="small"
                                  color={CCColors.TextColor.Dark}
                                />
                              )}
                              <CCText
                                fontSize={FontSizes[12]}
                                color={
                                  isCheckingMobileCoupon
                                    ? CCColors.TextColor.Grey
                                    : CCColors.TextColor.Dark
                                }
                                fontWeight="bold">
                                {isCheckingMobileCoupon
                                  ? 'Checking...'
                                  : 'Check'}
                              </CCText>
                            </Box>
                          </Pressable>
                        </Box>
                      )}
                      {mobileValidationError && (
                        <CCText
                          fontSize={FontSizes[12]}
                          color={CCColors.Red}
                          marginTop={6}>
                          {mobileValidationError}
                        </CCText>
                      )}
                    </Box>
                    {/* Your Selection Summary */}
                    <Box
                      padding={16}
                      backgroundColor={CCColors.White}
                      borderRadius={12}
                      borderWidth={1}
                      borderColor={CCColors.Grey.Divider200}
                      marginBottom={20}>
                      <CCText
                        fontSize={FontSizes[18]}
                        fontWeight="600"
                        color={CCColors.TextColor.Dark}
                        marginBottom={16}>
                        Your Selection
                      </CCText>

                      {/* Level */}
                      {selectedPricing?.level_name && (
                        <Box
                          flexDirection="row"
                          justifyContent="space-between"
                          marginBottom={12}>
                          <CCText
                            fontSize={FontSizes[14]}
                            color={CCColors.TextColor.Grey}>
                            Level
                          </CCText>
                          <CCText
                            fontSize={FontSizes[14]}
                            fontWeight="semibold"
                            color={CCColors.TextColor.Dark}>
                            {(selectedPricing.level_name &&
                            newUserPricingData?.meta
                              ?.is_user_preference_foundation
                              ? 'foundation'
                              : selectedPricing.level_name
                            )
                              ?.charAt(0)
                              ?.toUpperCase() +
                              (selectedPricing.level_name &&
                              newUserPricingData?.meta
                                ?.is_user_preference_foundation
                                ? 'foundation'
                                : selectedPricing.level_name
                              )?.slice(1)}
                          </CCText>
                        </Box>
                      )}

                      <Box
                        flexDirection="row"
                        justifyContent="space-between"
                        marginBottom={12}>
                        <CCText
                          fontSize={FontSizes[14]}
                          color={CCColors.TextColor.Grey}>
                          Plan Type
                        </CCText>
                        <CCText
                          fontSize={FontSizes[14]}
                          fontWeight="semibold"
                          color={CCColors.TextColor.Dark}>
                          {selectedPricing?.format_slug ===
                          'duel_group_2_students'
                            ? 'Personal Duel Group'
                            : selectedPlanType === 'personal'
                            ? 'Personal 1:1'
                            : selectedPlanType === 'group'
                            ? 'Small Group'
                            : 'Masterclass'}
                        </CCText>
                      </Box>

                      <Box
                        flexDirection="row"
                        justifyContent="space-between"
                        marginBottom={12}>
                        <CCText
                          fontSize={FontSizes[14]}
                          color={CCColors.TextColor.Grey}>
                          Duration
                        </CCText>
                        <CCText
                          fontSize={FontSizes[14]}
                          fontWeight="semibold"
                          color={CCColors.TextColor.Dark}>
                          {selectedDuration} Months
                        </CCText>
                      </Box>

                      <Box
                        flexDirection="row"
                        justifyContent="space-between"
                        marginBottom={12}>
                        <CCText
                          fontSize={FontSizes[14]}
                          color={CCColors.TextColor.Grey}>
                          Coach
                        </CCText>
                        <CCText
                          fontSize={FontSizes[14]}
                          fontWeight="semibold"
                          color={CCColors.TextColor.Dark}>
                          {selectedCoach === 'general'
                            ? 'Fide Rated Coach'
                            : selectedCoach === 'fide_master'
                            ? 'FIDE Master'
                            : selectedCoach === 'international_master'
                            ? 'International Master'
                            : 'Grand Master'}
                        </CCText>
                      </Box>

                      {/* Selected Batch for Group Plan */}
                      {selectedPlanType === 'group' && selectedBatch && (
                        <Box
                          marginTop={12}
                          paddingTop={12}
                          borderTopWidth={1}
                          borderTopColor={CCColors.Grey.Divider200}>
                          <CCText
                            fontSize={FontSizes[14]}
                            color={CCColors.TextColor.Grey}
                            marginBottom={8}>
                            Selected Batch
                          </CCText>
                          <Box
                            padding={12}
                            backgroundColor={CCColors.Primary.lightBrown}
                            borderRadius={8}
                            borderWidth={2}
                            borderColor={CCColors.Primary.Default}>
                            {/* Compact 2-line display: Line 1 - Batch Name + Coach */}
                            <Box
                              flexDirection="row"
                              flexWrap="wrap"
                              alignItems="center"
                              gap={8}
                              marginBottom={6}>
                              <CCText
                                fontSize={FontSizes[13]}
                                fontWeight="semibold"
                                color={CCColors.TextColor.Dark}
                                numberOfLines={1}
                                style={{flex: 1, minWidth: 120}}>
                                {selectedBatch.batch_name}
                              </CCText>
                              {selectedBatch.coach_name && (
                                <Box
                                  flexDirection="row"
                                  alignItems="center"
                                  gap={4}>
                                  <MaterialCommunityIcons
                                    name="account"
                                    size={12}
                                    color={CCColors.TextColor.Grey}
                                  />
                                  <CCText
                                    fontSize={FontSizes[11]}
                                    color={CCColors.TextColor.Grey}
                                    numberOfLines={1}>
                                    {selectedBatch.coach_name}
                                  </CCText>
                                </Box>
                              )}
                            </Box>
                            {/* Line 2 - Days + Time */}
                            {selectedBatch.class_days &&
                              selectedBatch.class_timing && (
                                <CCText
                                  fontSize={FontSizes[11]}
                                  color={CCColors.TextColor.Grey}
                                  numberOfLines={1}>
                                  {(() => {
                                    const dayMap: {[key: string]: string} = {
                                      '0': 'Sun',
                                      '1': 'Mon',
                                      '2': 'Tue',
                                      '3': 'Wed',
                                      '4': 'Thu',
                                      '5': 'Fri',
                                      '6': 'Sat',
                                    };
                                    const days = selectedBatch.class_days
                                      .split(',')
                                      .map(d => d.trim());
                                    const times = selectedBatch.class_timing
                                      .split(',')
                                      .map(t => t.trim());
                                    const lastTime =
                                      times[times.length - 1] || '';
                                    const pairs = days.map((day, index) => {
                                      const dayName = dayMap[day] || day;
                                      const time = times[index] || lastTime;
                                      return `${dayName}- ${time}`;
                                    });
                                    return pairs.join(', ');
                                  })()}
                                </CCText>
                              )}
                          </Box>
                        </Box>
                      )}

                      {/* Custom Slots for Group Plan */}
                      {selectedPlanType === 'group' &&
                        selectedSlots.length > 0 && (
                          <Box
                            marginTop={12}
                            paddingTop={12}
                            borderTopWidth={1}
                            borderTopColor={CCColors.Grey.Divider200}>
                            <CCText
                              fontSize={FontSizes[14]}
                              color={CCColors.TextColor.Grey}
                              marginBottom={8}>
                              Custom Time Slots ({selectedSlots.length})
                            </CCText>
                            <Box
                              padding={12}
                              backgroundColor={CCColors.Grey.Divider200}
                              borderRadius={10}
                              borderWidth={1}
                              borderColor={CCColors.BorderBottom}>
                              {selectedSlots.slice(0, 5).map((slot, index) => {
                                const slotDate = dayjs(slot.date);
                                const dayName = slotDate.format('ddd');
                                const dateStr = slotDate.format('MMM D');
                                const timeStr = slot.time.includes('T')
                                  ? dayjs(slot.time).format('h:mm A')
                                  : dayjs(`2000-01-01 ${slot.time}`).format(
                                      'h:mm A',
                                    );
                                return (
                                  <Box
                                    key={index}
                                    flexDirection="row"
                                    alignItems="center"
                                    gap={6}
                                    marginBottom={
                                      index <
                                      Math.min(selectedSlots.length, 5) - 1
                                        ? 8
                                        : 0
                                    }>
                                    <MaterialCommunityIcons
                                      name="calendar-clock"
                                      size={14}
                                      color={CCColors.Primary.Default}
                                    />
                                    <CCText
                                      fontSize={FontSizes[12]}
                                      color={CCColors.TextColor.Dark}>
                                      {dayName} {dateStr} at {timeStr}
                                    </CCText>
                                  </Box>
                                );
                              })}
                              {selectedSlots.length > 5 && (
                                <CCText
                                  fontSize={FontSizes[11]}
                                  color={CCColors.TextColor.Grey}
                                  marginTop={4}>
                                  +{selectedSlots.length - 5} more
                                </CCText>
                              )}
                              <Box
                                marginTop={8}
                                padding={8}
                                backgroundColor={CCColors.Primary.lightYellow}
                                borderRadius={6}>
                                <CCText
                                  fontSize={FontSizes[11]}
                                  color={CCColors.TextColor.Dark}
                                  lineHeight={16}>
                                  Our team will contact you to find the best
                                  batch matching your preferences.
                                </CCText>
                              </Box>
                            </Box>
                          </Box>
                        )}

                      {/* Selected Slots for Personal/Webinar Plan */}
                      {selectedSlots.length > 0 &&
                        selectedPlanType !== 'group' && (
                          <Box
                            marginTop={12}
                            paddingTop={12}
                            borderTopWidth={1}
                            borderTopColor={CCColors.Grey.Divider200}>
                            <CCText
                              fontSize={FontSizes[14]}
                              color={CCColors.TextColor.Grey}
                              marginBottom={8}>
                              Selected Time Slots ({selectedSlots.length})
                            </CCText>
                            {selectedSlots.slice(0, 3).map((slot, index) => {
                              const slotDate = dayjs(slot.date);
                              const dayName = slotDate.format('ddd');
                              const timeStr = slot.time.includes('T')
                                ? dayjs(slot.time).format('h:mm A')
                                : dayjs(`2000-01-01 ${slot.time}`).format(
                                    'h:mm A',
                                  );
                              return (
                                <CCText
                                  key={index}
                                  fontSize={FontSizes[12]}
                                  color={CCColors.TextColor.Dark}
                                  marginBottom={4}>
                                  {dayName} at {timeStr}
                                </CCText>
                              );
                            })}
                            {selectedSlots.length > 3 && (
                              <CCText
                                fontSize={FontSizes[12]}
                                color={CCColors.TextColor.Grey}>
                                +{selectedSlots.length - 3} more
                              </CCText>
                            )}
                          </Box>
                        )}
                    </Box>

                    {/* Amount Breakdown */}
                    <Box
                      padding={16}
                      backgroundColor={CCColors.White}
                      borderRadius={12}
                      borderWidth={1}
                      borderColor={CCColors.Grey.Divider200}>
                      <CCText
                        fontSize={FontSizes[18]}
                        fontWeight="600"
                        color={CCColors.TextColor.Dark}
                        marginBottom={16}>
                        Amount Breakdown
                      </CCText>

                      <Box
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        marginBottom={10}>
                        <CCText
                          fontSize={FontSizes[14]}
                          color={CCColors.TextColor.Dark}>
                          Base Fee
                          {breakdown?.currency === 'INR' ? ' (incl. GST)' : ''}
                        </CCText>
                        <Box alignItems="flex-end">
                          <Box flexDirection="row" alignItems="center" gap={8}>
                            <CCText
                              fontSize={FontSizes[14]}
                              fontWeight="semibold"
                              color={CCColors.TextColor.Dark}>
                              {formatCurrency(
                                breakdown?.maxBasePrice ||
                                  breakdown?.basePrice ||
                                  0,
                                breakdown?.currency || 'INR',
                              )}
                            </CCText>
                            {breakdown?.offerDiscountPercentage &&
                            breakdown.offerDiscountPercentage > 0 ? (
                              <Box
                                backgroundColor={CCColors.Orange}
                                paddingHorizontal={6}
                                paddingVertical={2}
                                borderRadius={4}>
                                <CCText
                                  fontSize={FontSizes[10]}
                                  color={CCColors.White}
                                  fontWeight="bold">
                                  OFFER
                                </CCText>
                              </Box>
                            ) : (
                              <></>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {breakdown && breakdown.durationDiscount > 0 && (
                        <Box
                          flexDirection="row"
                          justifyContent="space-between"
                          marginBottom={10}>
                          <CCText
                            fontSize={FontSizes[14]}
                            color={CCColors.TextColor.Dark}>
                            Duration Discount
                            {breakdown.durationDiscountPct > 0
                              ? ` (${breakdown.durationDiscountPct}% off)`
                              : ''}
                          </CCText>
                          <CCText
                            fontSize={FontSizes[14]}
                            fontWeight="semibold"
                            color={CCColors.Green}>
                            -
                            {formatCurrency(
                              breakdown.durationDiscount,
                              breakdown.currency,
                            )}
                          </CCText>
                        </Box>
                      )}

                      {breakdown && breakdown.couponDiscount > 0 && (
                        <Box
                          flexDirection="row"
                          justifyContent="space-between"
                          marginBottom={10}>
                          <CCText
                            fontSize={FontSizes[14]}
                            color={CCColors.TextColor.Dark}>
                            Coupon Discount
                          </CCText>
                          <CCText
                            fontSize={FontSizes[14]}
                            fontWeight="semibold"
                            color={CCColors.Green}>
                            -
                            {formatCurrency(
                              breakdown.couponDiscount,
                              breakdown.currency,
                            )}
                          </CCText>
                        </Box>
                      )}

                      {breakdown &&
                        breakdown.currency === 'INR' &&
                        breakdown.gst > 0 && (
                          <Box
                            flexDirection="row"
                            justifyContent="space-between"
                            marginBottom={10}>
                            <CCText
                              fontSize={FontSizes[14]}
                              color={CCColors.TextColor.Dark}>
                              GST (18%){' '}
                              <CCText
                                fontSize={FontSizes[12]}
                                color={CCColors.TextColor.Grey}>
                                (included)
                              </CCText>
                            </CCText>
                            <CCText
                              fontSize={FontSizes[14]}
                              fontWeight="semibold"
                              color={CCColors.TextColor.Dark}>
                              {formatCurrency(
                                breakdown.gst,
                                breakdown.currency,
                              )}
                            </CCText>
                          </Box>
                        )}

                      <Box
                        height={1}
                        backgroundColor={CCColors.Grey.Divider200}
                        marginVertical={12}
                      />

                      {/* View Breakup Button */}
                      {selectedPricing &&
                        selectedDuration &&
                        (() => {
                          const roadmap = selectedPricing.roadmaps?.find(
                            r => r.duration_months === selectedDuration,
                          );
                          return roadmap &&
                            roadmap.segments &&
                            roadmap.segments.length > 0 ? (
                            <Box marginBottom={16} alignItems="flex-end">
                              <ViewBreakupModal
                                segments={roadmap.segments}
                                totalPrice={
                                  roadmap.max_total_price || roadmap.total_price
                                }
                                currency={
                                  renewalData?.currency ||
                                  selectedPricing.currency_code ||
                                  selectedPricing.currency ||
                                  'INR'
                                }
                                title={`${selectedDuration} Months Roadmap`}
                                buttonLabel="View Breakup"
                              />
                            </Box>
                          ) : null;
                        })()}

                      <Box
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center">
                        <CCText
                          fontSize={FontSizes[16]}
                          fontWeight="bold"
                          color={CCColors.TextColor.Dark}>
                          Grand Total
                        </CCText>
                        <Box alignItems="flex-end">
                          <CCText
                            fontSize={FontSizes[20]}
                            fontWeight="bold"
                            color={CCColors.Primary.Default}>
                            {formatCurrency(
                              breakdown?.grandTotal || 0,
                              breakdown?.currency || 'INR',
                            )}
                          </CCText>
                          <CCText
                            fontSize={FontSizes[12]}
                            color={CCColors.TextColor.Grey}>
                            (
                            {formatCurrency(
                              breakdown?.monthlyEquivalent || 0,
                              breakdown?.currency || 'INR',
                            )}{' '}
                            per month)
                          </CCText>
                        </Box>
                      </Box>
                    </Box>

                    {/* Billing Address (non-INR only) */}
                    {isNonINR ? (
                      <View ref={billingMobileSectionRef}>
                      <Box
                        padding={20}
                        backgroundColor={CCColors.White}
                        borderRadius={12}
                        borderWidth={1}
                        borderColor={CCColors.Grey.Divider200}
                        marginTop={20}>
                        <Box
                          flexDirection="row"
                          alignItems="center"
                          gap={8}
                          marginBottom={20}>
                          <MaterialCommunityIcons
                            name="map-marker-outline"
                            size={20}
                            color={CCColors.Primary.Default}
                          />
                          <CCText
                            fontSize={FontSizes[16]}
                            fontWeight="bold"
                            color={CCColors.TextColor.Dark}>
                            Billing Address
                          </CCText>
                        </Box>
                        <Box flexDirection="row" gap={12} marginBottom={16}>
                          <Box flex={1}>
                            <CCText
                              fontSize={FontSizes[13]}
                              fontWeight="700"
                              color={CCColors.TextColor.Dark}
                              marginBottom={6}>
                              First Name{' '}
                              <CCText
                                fontSize={FontSizes[16]}
                                fontWeight="bold"
                                color={CCColors.Red}>
                                *
                              </CCText>
                            </CCText>
                            <TextInput
                              placeholder="First name"
                              placeholderTextColor="#6B7280"
                              value={billingFirstName}
                              onChangeText={value => {
                                setBillingFirstName(value);
                                clearBillingError();
                              }}
                              style={styles.billingInput}
                            />
                          </Box>
                          <Box flex={1}>
                            <CCText
                              fontSize={FontSizes[13]}
                              fontWeight="700"
                              color={CCColors.TextColor.Dark}
                              marginBottom={6}>
                              Last Name{' '}
                              <CCText
                                fontSize={FontSizes[16]}
                                fontWeight="bold"
                                color={CCColors.Red}>
                                *
                              </CCText>
                            </CCText>
                            <TextInput
                              placeholder="Last name"
                              placeholderTextColor="#6B7280"
                              value={billingLastName}
                              onChangeText={value => {
                                setBillingLastName(value);
                                clearBillingError();
                              }}
                              style={styles.billingInput}
                            />
                          </Box>
                        </Box>
                        <Box marginBottom={16}>
                          <CCText
                            fontSize={FontSizes[13]}
                            fontWeight="700"
                            color={CCColors.TextColor.Dark}
                            marginBottom={6}>
                            Address{' '}
                            <CCText
                              fontSize={FontSizes[16]}
                              fontWeight="bold"
                              color={CCColors.Red}>
                              *
                            </CCText>
                          </CCText>
                          <TextInput
                            placeholder="Street address, apartment, suite"
                            placeholderTextColor="#6B7280"
                            value={billingLine1}
                            onChangeText={value => {
                              setBillingLine1(value);
                              clearBillingError();
                            }}
                            style={styles.billingInput}
                          />
                        </Box>
                        <Box marginBottom={8}>
                          <CCText
                            fontSize={FontSizes[13]}
                            fontWeight="700"
                            color={CCColors.TextColor.Dark}
                            marginBottom={6}>
                            Country{' '}
                            <CCText
                              fontSize={FontSizes[16]}
                              fontWeight="bold"
                              color={CCColors.Red}>
                              *
                            </CCText>
                          </CCText>
                          <CCSelect
                            useAsync
                            placeholder="Select country"
                            options={billingCountryOptions}
                            value={selectedBillingCountry}
                            onChange={(option: any) => {
                              setBillingCountry(option?.value || '');
                              clearBillingError();
                            }}
                            AsyncStyles={billingCountrySelectStyles}
                          />
                        </Box>
                        {billingError ? (
                          <Box style={styles.billingErrorBox}>
                            <MaterialCommunityIcons
                              name="alert-circle"
                              size={16}
                              color={CCColors.Red}
                            />
                            <CCText
                              fontSize={FontSizes[12]}
                              color={CCColors.Red}
                              style={styles.billingErrorText}>
                              {billingError}
                            </CCText>
                          </Box>
                        ) : (
                          <CCText
                            fontSize={FontSizes[13]}
                            fontWeight="600"
                            color={CCColors.TextColor.Dark}
                            style={styles.billingHelperText}>
                            Required for international payments. Your billing
                            info is kept secure.
                          </CCText>
                        )}
                      </Box>
                      </View>
                    ) : null}
                  </Box>
                </>
              )}
            </Box>
          </ScrollView>

          {/* Floating Action Button */}
          <View style={styles.floatingButtonContainer}>
            {mobileStage === 3 ? (
              <Box
                flexDirection="row"
                gap={12}
                alignItems="center"
                justifyContent="space-between"
                width="100%">
                {/* Back Button */}
                {mobileStage > 1 && (
                  <Pressable
                    onPress={handleBackStage}
                    style={[styles.floatingButton, styles.backButton]}>
                    <MaterialCommunityIcons
                      name="arrow-left"
                      size={24}
                      color={CCColors.TextColor.Primary}
                    />
                  </Pressable>
                )}
                {/* Request Callback Button */}
                <Pressable
                  onPress={handleRequestCallback}
                  disabled={isRequestingCallback || callbackRequested}
                  style={[
                    styles.floatingButton,
                    styles.nextButton,
                    styles.payButton,
                    mobileStage > 1 && {flex: 0.3},
                  ]}>
                  {isRequestingCallback ? (
                    <ActivityIndicator
                      size="small"
                      color={CCColors.TextColor.Primary}
                    />
                  ) : callbackRequested ? (
                    <MaterialCommunityIcons
                      name="check"
                      size={24}
                      color={CCColors.TextColor.Primary}
                    />
                  ) : (
                    <MaterialCommunityIcons
                      name="phone"
                      size={24}
                      color={CCColors.TextColor.Primary}
                    />
                    // <CCText
                    //   fontSize={FontSizes[16]}
                    //   fontWeight="bold"
                    //   color={CCColors.TextColor.Primary}>
                    //   Request Callback
                    // </CCText>
                  )}
                </Pressable>
                {/* Pay Button */}
                <Pressable
                  onPress={handleMobilePay}
                  disabled={!breakdown || breakdown.grandTotal <= 0}
                  style={[
                    styles.floatingButton,
                    styles.nextButton,
                    styles.payButton,
                    mobileStage > 1 && {flex: 1},
                    (!breakdown || breakdown.grandTotal <= 0) &&
                      styles.disabledButton,
                  ]}>
                  <Box flexDirection="row" alignItems="center" gap={8}>
                    <MaterialCommunityIcons
                      name="lock"
                      size={20}
                      color={CCColors.TextColor.Primary}
                    />
                    <CCText
                      fontSize={FontSizes[16]}
                      fontWeight="bold"
                      color={CCColors.TextColor.Primary}>
                      Pay{' '}
                      {formatCurrency(
                        breakdown?.grandTotal || 0,
                        breakdown?.currency || 'INR',
                      )}
                    </CCText>
                  </Box>
                </Pressable>
              </Box>
            ) : (
              <Box
                flexDirection="row"
                gap={12}
                alignItems="center"
                width="100%">
                {/* Back Button */}
                {mobileStage > 1 && (
                  <Pressable
                    onPress={handleBackStage}
                    style={[styles.floatingButton, styles.backButton]}>
                    <MaterialCommunityIcons
                      name="arrow-left"
                      size={24}
                      color={CCColors.TextColor.Primary}
                    />
                  </Pressable>
                )}
                {/* Next Button */}
                <Pressable
                  onPress={handleNextStage}
                  style={[
                    styles.floatingButton,
                    styles.nextButton,
                    mobileStage > 1 && {flex: 1},
                  ]}>
                  <Box flexDirection="row" alignItems="center" gap={8}>
                    <Box flexDirection="row" alignItems="center" gap={4}>
                      <CCText
                        fontSize={FontSizes[16]}
                        fontWeight="bold"
                        color={CCColors.TextColor.Primary}>
                        {mobileStage === 1 &&
                        selectedPricing &&
                        basePriceForButton > 0
                          ? `Next - ${formatCurrency(
                              basePriceForButton,
                              renewalData?.currency ||
                                selectedPricing.currency_code ||
                                selectedPricing.currency ||
                                'INR',
                            )}`
                          : mobileStage === 2 &&
                            selectedDuration &&
                            basePriceForButton > 0
                          ? `Next - ${formatCurrency(
                              basePriceForButton,
                              pricingBreakdown?.currency ||
                                renewalData?.currency ||
                                selectedPricing?.currency_code ||
                                'INR',
                            )}`
                          : 'Next'}
                      </CCText>
                      {(mobileStage === 1 &&
                        selectedPricing &&
                        basePriceForButton > 0 &&
                        pricingBreakdown?.offerDiscountPercentage &&
                        pricingBreakdown.offerDiscountPercentage > 0) ||
                      (mobileStage === 2 &&
                        selectedDuration &&
                        basePriceForButton > 0 &&
                        pricingBreakdown?.offerDiscountPercentage &&
                        pricingBreakdown.offerDiscountPercentage > 0) ? (
                        <Box
                          backgroundColor={CCColors.Orange}
                          paddingHorizontal={4}
                          paddingVertical={1}
                          borderRadius={3}>
                          <CCText
                            fontSize={FontSizes[9]}
                            color={CCColors.White}
                            fontWeight="bold">
                            OFFER
                          </CCText>
                        </Box>
                      ) : (
                        <></>
                      )}
                    </Box>
                    <MaterialCommunityIcons
                      name="arrow-right"
                      size={24}
                      color={CCColors.TextColor.Primary}
                    />
                  </Box>
                </Pressable>
              </Box>
            )}
          </View>
        </View>
      ) : (
        <Box flex={1} flexDirection="row" style={styles.webContainer}>
          {/* Left Column - Scrollable */}
          <ScrollView
            ref={scrollViewRef}
            style={styles.leftScrollContainer}
            contentContainerStyle={styles.leftScrollContent}
            showsVerticalScrollIndicator={true}>
            <Box paddingHorizontal={40} paddingTop={20} paddingBottom={40}>
              <Box flexDirection="row" gap={10} alignItems="center">
                {level ? (
                  <Box
                    flexDirection="row"
                    alignItems="center"
                    style={{
                      backgroundColor: '#FDF1C8',
                      paddingHorizontal: 14,
                      paddingVertical: 6,
                      borderRadius: 20,
                      alignSelf: 'flex-start',
                      marginVertical: 8,
                      borderWidth: 1,
                      borderColor: '#FDE394',
                    }}
                    gap={6}>
                    <MaterialCommunityIcons
                      name="gauge"
                      size={20}
                      color={CCColors.Primary.Brown}
                    />
                    <CCText
                      fontSize={FontSizes[18]}
                      fontWeight="bold"
                      color={CCColors.Primary.Brown}>
                      Level:{' '}
                      {(level &&
                      newUserPricingData?.meta?.is_user_preference_foundation
                        ? 'foundation'
                        : level
                      )
                        ?.charAt(0)
                        ?.toUpperCase() +
                        (level &&
                        newUserPricingData?.meta?.is_user_preference_foundation
                          ? 'foundation'
                          : level
                        )?.slice(1)}
                    </CCText>
                  </Box>
                ) : (
                  <CCText
                    fontSize={FontSizes[24]}
                    fontWeight="bold"
                    color={CCColors.TextColor.Dark}
                    marginBottom={20}>
                    {!level && 'Level Roadmap'}
                  </CCText>
                )}
              </Box>

              {/* Level Tracker (hidden for new-user flow) */}
              {!level &&
                (levels.length > 0 ? (
                  <Box>
                    <LevelTracker levels={levels} />
                  </Box>
                ) : (
                  <Box paddingVertical={20} marginBottom={30}>
                    <CCText
                      fontSize={FontSizes[14]}
                      color={CCColors.TextColor.Grey}
                      textAlign="center">
                      Loading levels...
                    </CCText>
                  </Box>
                ))}

              {/* Step 1: Level/Plan Selection */}
              <Box marginBottom={30}>
                <CCText
                  fontSize={FontSizes[18]}
                  fontWeight="600"
                  color={CCColors.TextColor.Dark}
                  marginBottom={16}>
                  Select Your Plan
                </CCText>
                <Spacer spacing={10} />
                <PlanSelector
                  selectedPlan={selectedPlanType}
                  selectedPricingId={selectedPricingId}
                  selectedLevelSlug={selectedLevelSlug}
                  onSelect={handlePlanTypeSelect}
                  selectedBatch={selectedBatch}
                  onSelectTiming={handleSelectCustomTiming}
                  onViewWebinarDetails={() => setShowWebinarDetailsModal(true)}
                  isMobile={isMobileOrTab}
                  planPerks={selectedPricing?.plan_perks || []}
                  recommendedPlan={
                    renewalData?.pricing?.find(p => p.is_recommended)
                      ?.plan_type as
                      | 'personal'
                      | 'group'
                      | 'webinar'
                      | undefined
                  }
                  showCurrentTag={!isNewPaymentPage}
                  prioritizeSelectedAtTop={isNewPaymentPage}
                  availablePlanTypes={availablePlanTypes}
                  availablePlanOptions={availablePlanOptions}
                  webinarOptions={
                    selectedPlanType === 'webinar' &&
                    selectedPricing?.webinar_options
                      ? selectedPricing.webinar_options
                      : []
                  }
                  isFoundationPreference={
                    newUserPricingData?.meta?.is_user_preference_foundation
                  }
                />

                {/* Coach Selection - Above Duration */}
                {selectedPricing && (
                  <View ref={coachSelectorRef}>
                    <CoachSelector
                      selectedCoach={selectedCoach}
                      onSelect={handleCoachSelect}
                      isMobile={isMobileOrTab}
                      premiumAmount={pricingBreakdown?.coachPremium || 0}
                      currency={
                        pricingBreakdown?.currency ||
                        renewalData?.currency ||
                        'INR'
                      }
                      currentCoach={
                        (renewalData?.current_subscription
                          ?.coach_type as CoachType) || 'general'
                      }
                      coachPerks={selectedPricing?.coach_perks || []}
                      availableCoachTiers={availableCoachTiers}
                    />
                  </View>
                )}

                <View ref={durationSelectorRef}>
                  <DurationSelector
                    selectedDuration={selectedDuration}
                    onSelect={handleDurationSelect}
                    pricingOptions={pricingOptions}
                    isMobile={isMobileOrTab}
                    selectedCoach={selectedCoach}
                    availableDurations={renewalData?.available_durations}
                    durationDiscounts={renewalData?.duration_discounts}
                    tournamentId={selectedPricing?.tournament_id}
                  />
                </View>
              </Box>
            </Box>
          </ScrollView>

          {/* Right Column (Order Summary) - Fixed/Sticky */}
          <Box width={350} style={styles.rightStickyContainer}>
            <CCText
              fontSize={FontSizes[18]}
              fontWeight="600"
              color={CCColors.TextColor.Dark}
              marginBottom={16}>
              Step 3: Review & Pay
            </CCText>
            <Spacer spacing={10} />
            {/* Billing Address (non-INR only) - Desktop */}
            {isNonINR ? (
              <View ref={billingDesktopSectionRef}>
              <Box
                padding={20}
                backgroundColor={CCColors.White}
                borderRadius={12}
                borderWidth={1}
                width={350}
                borderColor={CCColors.Grey.Divider200}
                style={styles.cardShadow}>
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap={8}
                  marginBottom={20}>
                  <MaterialCommunityIcons
                    name="map-marker-outline"
                    size={20}
                    color={CCColors.Primary.Default}
                  />
                  <CCText
                    fontSize={FontSizes[16]}
                    fontWeight="bold"
                    color={CCColors.TextColor.Dark}>
                    Billing Address
                  </CCText>
                </Box>
                <Box flexDirection="row" gap={12} marginBottom={16}>
                  <Box flex={1}>
                    <CCText
                      fontSize={FontSizes[13]}
                      fontWeight="700"
                      color={CCColors.TextColor.Dark}
                      marginBottom={6}>
                      First Name{' '}
                      <CCText
                        fontSize={FontSizes[16]}
                        fontWeight="bold"
                        color={CCColors.Red}>
                        *
                      </CCText>
                    </CCText>
                    <TextInput
                      placeholder="First name"
                      placeholderTextColor="#6B7280"
                      value={billingFirstName}
                      onChangeText={value => {
                        setBillingFirstName(value);
                        clearBillingError();
                      }}
                      style={styles.billingInput}
                    />
                  </Box>
                  <Box flex={1}>
                    <CCText
                      fontSize={FontSizes[13]}
                      fontWeight="700"
                      color={CCColors.TextColor.Dark}
                      marginBottom={6}>
                      Last Name{' '}
                      <CCText
                        fontSize={FontSizes[16]}
                        fontWeight="bold"
                        color={CCColors.Red}>
                        *
                      </CCText>
                    </CCText>
                    <TextInput
                      placeholder="Last name"
                      placeholderTextColor="#6B7280"
                      value={billingLastName}
                      onChangeText={value => {
                        setBillingLastName(value);
                        clearBillingError();
                      }}
                      style={styles.billingInput}
                    />
                  </Box>
                </Box>
                <Box marginBottom={16}>
                  <CCText
                    fontSize={FontSizes[13]}
                    fontWeight="700"
                    color={CCColors.TextColor.Dark}
                    marginBottom={6}>
                    Address{' '}
                    <CCText
                      fontSize={FontSizes[16]}
                      fontWeight="bold"
                      color={CCColors.Red}>
                      *
                    </CCText>
                  </CCText>
                  <TextInput
                    placeholder="Street address, apartment, suite"
                    placeholderTextColor="#6B7280"
                    value={billingLine1}
                    onChangeText={value => {
                      setBillingLine1(value);
                      clearBillingError();
                    }}
                    style={styles.billingInput}
                  />
                </Box>
                <Box marginBottom={8}>
                  <CCText
                    fontSize={FontSizes[13]}
                    fontWeight="700"
                    color={CCColors.TextColor.Dark}
                    marginBottom={6}>
                    Country{' '}
                    <CCText
                      fontSize={FontSizes[16]}
                      fontWeight="bold"
                      color={CCColors.Red}>
                      *
                    </CCText>
                  </CCText>
                  <CCSelect
                    useAsync
                    placeholder="Select country"
                    options={billingCountryOptions}
                    value={selectedBillingCountry}
                    onChange={(option: any) => {
                      setBillingCountry(option?.value || '');
                      clearBillingError();
                    }}
                    AsyncStyles={billingCountrySelectStyles}
                  />
                </Box>
                {billingError ? (
                  <Box style={styles.billingErrorBox}>
                    <MaterialCommunityIcons
                      name="alert-circle"
                      size={16}
                      color={CCColors.Red}
                    />
                    <CCText
                      fontSize={FontSizes[12]}
                      color={CCColors.Red}
                      style={styles.billingErrorText}>
                      {billingError}
                    </CCText>
                  </Box>
                ) : (
                  <CCText
                    fontSize={FontSizes[13]}
                    fontWeight="600"
                    color={CCColors.TextColor.Dark}
                    style={styles.billingHelperText}>
                    Required for international payments. Your billing info is
                    kept secure.
                  </CCText>
                )}
              </Box>
              </View>
            ) : null}
            <OrderSummary
              baseFee={breakdown?.maxBasePrice || breakdown?.basePrice || 0}
              maxBasePrice={breakdown?.maxBasePrice}
              offerPrice={breakdown?.offerPrice}
              offerDiscountAmount={breakdown?.offerDiscountAmount}
              offerDiscountPercentage={breakdown?.offerDiscountPercentage}
              coachPremium={breakdown?.coachPremium || 0}
              discount={breakdown?.durationDiscount || 0}
              discountPct={breakdown?.durationDiscountPct || 0}
              couponDiscount={breakdown?.couponDiscount || 0}
              gst={breakdown?.gst || 0}
              total={breakdown?.grandTotal || 0}
              currency={breakdown?.currency || renewalData?.currency || 'INR'}
              onPay={handlePayNow}
              isMobile={isMobileOrTab}
              selectedPlanType={selectedPlanType}
              selectedDuration={selectedDuration}
              selectedSlots={selectedSlots}
              selectedBatch={selectedBatch}
              onRemoveSlot={handleRemoveSlot}
              onViewCoupons={() => setShowCouponModal(true)}
              onRemoveCoupon={handleRemoveCoupon}
              onApplyCoupon={handleApplyCoupon}
              appliedCoupon={appliedCoupon}
              tournamentId={
                selectedPricing?.tournament_id ||
                renewalData?.current_subscription?.tournament_id
              }
              selectedPricing={selectedPricing}
              selectedCoach={selectedCoach}
              renewalData={renewalData}
              isCheckoutPage={isNewPaymentPage}
              is_user_preference_foundation={
                newUserPricingData?.meta?.is_user_preference_foundation
              }
            />
          </Box>
        </Box>
      )}

      <DateTimeSlotModal
        visible={showSlotModal}
        onClose={() => setShowSlotModal(false)}
        onSelect={handleSlotSelect}
        selectedSlots={selectedSlots}
        allowMultiple={true}
        isCustomTiming={isCustomTiming}
      />

      <BatchSelectionModal
        visible={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        onSelect={handleBatchSelect}
        selectedBatch={selectedBatch}
        tournamentId={
          // Get next level tournament_id from selectedPricing.next_level_pricing_ids
          // First try to get from next_level_pricing_ids, then fallback to selectedPricing.tournament_id
          (() => {
            if (
              selectedPricing?.next_level_pricing_ids &&
              selectedPricing.next_level_pricing_ids.length > 0
            ) {
              // Find the first pricing entry that matches group format
              const nextPricingId = selectedPricing.next_level_pricing_ids[0];
              const nextPricing = renewalData?.pricing?.find(
                p => p.id === nextPricingId,
              );
              if (nextPricing?.tournament_id) {
                return nextPricing.tournament_id;
              }
            }
            // Fallback to selectedPricing tournament_id or current subscription tournament_id
            return (
              selectedPricing?.tournament_id ||
              renewalData?.current_subscription?.tournament_id
            );
          })()
        }
        planExpiryDate={renewalData?.current_subscription?.end_date}
        onOpenCustomTiming={handleOpenCustomTimingFromBatch}
        currentCoachName={null} // Now comes from API response, this is just a fallback
      />
      <CouponModal
        visible={showCouponModal}
        onClose={() => setShowCouponModal(false)}
        onApplyCoupon={handleApplyCoupon}
        tournamentId={
          selectedPricing?.tournament_id ||
          renewalData?.current_subscription?.tournament_id
        }
        planType={selectedPlanType}
        durationMonths={selectedDuration}
        appliedCouponCode={appliedCoupon?.code}
        checkoutPage={isNewPaymentPage}
      />

      {breakdown && (
        <PaymentConfirmationModal
          visible={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onPaymentSuccess={orderId => {
            setOrderId(orderId);
            setShowPaymentModal(false);
            setIsProcessingPayment(true); // Start 2-second loader
          }}
          renewalData={renewalData}
          selectedPlanType={selectedPlanType}
          selectedDuration={selectedDuration}
          selectedSlots={selectedSlots}
          selectedCoach={selectedCoach}
          selectedBatch={selectedBatch}
          pricingBreakdown={breakdown}
          appliedCoupon={appliedCoupon}
          selectedPricing={selectedPricing}
          selectedPricingId={selectedPricingId}
          isNewUser={Boolean(level)}
          is_user_preference_foundation={
            newUserPricingData?.meta?.is_user_preference_foundation
          }
          user_level_source={newUserPricingData?.meta?.user_level_source}
          initialBillingFirstName={billingFirstName}
          initialBillingLastName={billingLastName}
          initialBillingLine1={billingLine1}
          initialBillingCountry={billingCountry}
        />
      )}

      {/* Support Required Modal - Non-closable */}
      <SupportRequiredModal
        visible={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />

      {/* Webinar Details Modal */}
      <WebinarDetailsModal
        visible={showWebinarDetailsModal}
        onClose={() => setShowWebinarDetailsModal(false)}
        planPerks={selectedPricing?.plan_perks || []}
        webinarOptions={
          selectedPlanType === 'webinar' && selectedPricing?.webinar_options
            ? selectedPricing.webinar_options
            : []
        }
        levelName={selectedPricing?.level_name}
      />

      {/* Absolute Call Button */}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    // flex: 1,
    height: '85vh',
    backgroundColor: CCColors.CCTableBg,
    // backgroundColor: 'red',
  },
  contentContainer: {
    paddingBottom: 120, // Increased to account for floating button (80px button + 40px padding)
  },
  webContainer: {
    height: '90vh',
    backgroundColor: CCColors.CCTableBg,
    // overflow: 'hidden',
  },
  leftScrollContainer: {
    flex: 1,
  },
  leftScrollContent: {
    flexGrow: 1,
  },
  rightStickyContainer: {
    ...(Platform.OS === 'web'
      ? {
          position: 'sticky' as any,
          top: 0,
          alignSelf: 'flex-start' as any,
          // maxHeight: '100vh' as any,
          overflowY: 'auto' as any,
        }
      : {}),
    paddingRight: 40,
    paddingTop: 20,
  },

  floatingButtonContainer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    paddingHorizontal: 15,
    paddingVertical: 12,
    paddingBottom: Platform.OS === 'ios' ? 30 : 12,
    backgroundColor: CCColors.White,
    borderTopWidth: 1,
    borderTopColor: CCColors.Grey.Divider200,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: -2},
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 1000,
    gap: 12,
    width: '100%',
    // ...(Platform.OS === 'web'
    //   ? {
    //       position: 'fixed' as any,
    //     }
    //   : {}),
  },
  floatingButton: {
    flex: 1,
    height: 50,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  // backButton: {
  //   flex: 0,
  //   width: 50,
  //   backgroundColor: CCColors.Grey.Divider200,
  // },
  nextButton: {
    backgroundColor: CCColors.Primary.Default,
  },
  backButton: {
    flex: 0.2,
    backgroundColor: CCColors.Grey.Divider200,
  },
  payButton: {
    backgroundColor: CCColors.Primary.Default,
  },
  disabledButton: {
    opacity: 0.5,
  },
  callbackButton: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: CCColors.Primary.Default,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  callbackButtonDisabled: {
    opacity: 0.6,
  },
  couponInput: {
    flex: 1,
    fontSize: FontSizes[14],
    color: CCColors.TextColor.Dark,
  },
  couponCheckButton: {
    paddingHorizontal: 12,
    height: 32,
    borderRadius: 6,
    backgroundColor: CCColors.Primary.lightBrown,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: CCColors.Grey.Divider200,
  },
  couponCheckButtonDisabled: {
    opacity: 0.5,
  },
  billingInput: {
    borderWidth: 1,
    borderColor: CCColors.Grey.Divider200,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: FontSizes[15],
    color: CCColors.TextColor.Dark,
    backgroundColor: CCColors.White,
    shadowColor: '#4D3F37',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 1,
  },
  billingHelperText: {
    marginTop: 10,
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FDE394',
    backgroundColor: '#FFF8E1',
    lineHeight: 18,
  },
  billingErrorBox: {
    marginTop: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: CCColors.Red,
    backgroundColor: '#FFF4F4',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  billingErrorText: {
    flex: 1,
    lineHeight: 18,
  },
  cardShadow: {
    shadowColor: '#4D3F37',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
    marginBottom: 20,
  },
});

export default RenewalScreen;
